import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  fetchCustomersWithUninvoicedSales,
  fetchLastInvoiceDateByCustomer,
  fetchUninvoicedSalesByCustomerBranch,
  fetchInvoicesWithBalances,
  fetchDraftInvoices,
  deleteDraftInvoice,
  syncDraftStatus,
  updateInvoicePaidStatus,
  disregardSale,
  fetchXeroConfig,
  buildXeroInvoiceUrl,
  sendInvoicesToXero,
  isCustomerMappedToXero,
  isCustomerMappedToGoCardless,
  fetchNextGoCardlessPayout,
  // markInvoiceAsPaid moved to MarkAsPaidDialog component (US-REC-001)
  markSalesAsPaidWithoutInvoice,
  revertDraftInvoice,
  authoriseAndSendBatch,
  retryInvoiceEmail,
  fetchProductPrices,
  fetchVolumeDiscounts,
  supabase
} from '../supabase-client'
import { retryXeroApiCall } from '../utils/retry'
// InvoicePDFGenerator removed - Xero generates PDFs (Task 5 v2)
import SendInvoiceEmailDialog from './invoice/SendInvoiceEmailDialog.jsx'
import MarkAsPaidDialog from './invoice/MarkAsPaidDialog.jsx'
import CustomerMappingModal from './CustomerMappingModal'
// US-REC-001: Invoice Reconciliation Components
import { InvoiceStatusBadge } from './invoices/InvoiceStatusBadge'
import { InvoiceDetailPanel } from './invoices/InvoiceDetailPanel'
import { CreditNoteModal } from './credit-notes/CreditNoteModal'
import { getTodayISOString, parseDateString } from '../utils/dateUtils.js'
import { formatInvoiceDisplayName } from '../utils/invoiceUtils.js'
import { PageLayout } from './layout'
// InvoicePreviewModal removed - Direct Xero creation (Task 5 v2)
import HistoricalInvoiceModal from './HistoricalInvoiceModal'
import { Button } from '@/components/ui/button'
import SearchInput from '@/components/ui/search-input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { ExternalLink, CheckCircle, XCircle, ChevronLeft, ChevronRight, RotateCcw, AlertTriangle, RefreshCw, CircleCheck, Circle, CircleAlert, List, Eye, ReceiptIcon, MoreHorizontal, Plus, X, Loader2, Check } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { addDays, parseISO, isAfter, differenceInDays, format } from 'date-fns'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import ToggleSwitch from './ToggleSwitch'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DecimalInput } from '@/components/ui/decimal-input'
import { toast } from 'sonner'
import { CustomerDetailModal } from './customer/CustomerDetailModal'
import { XeroSyncDiffDialog } from './xero/XeroSyncDiffDialog'
import { fetchCustomer } from '../supabase-client'
import './InvoiceDashboard.css'

// Enhanced Review Modal Component (Task 12: Full review/edit capabilities)
function ReviewModal({ isOpen, onClose, reviewData, customerName, customerEmail: customerEmailProp, customerBranches, selectedBranchIds, onBranchSelectionChange, onGenerateInvoice, onDisregardSale, onDraftCreated, onUninvoicedDataRefresh, onDataRefresh, onMarkAsPaid, onEmailWarning, onCustomerClick }) {
  const [saleToDisregard, setSaleToDisregard] = useState(null)
  const [showDisregardDialog, setShowDisregardDialog] = useState(false)
  const [showSendConfirm, setShowSendConfirm] = useState(false)
  const [showCashConfirm, setShowCashConfirm] = useState(false)
  const [isMarkingPaid, setIsMarkingPaid] = useState(false)
  const [pendingDisregard, setPendingDisregard] = useState(false)

  // Task 5.1: Customer mapping state
  const [isCustomerMapped, setIsCustomerMapped] = useState(false)
  const [checkingMapping, setCheckingMapping] = useState(true)
  const [showMappingModal, setShowMappingModal] = useState(false)
  const customerId = reviewData?.[0]?.customer_id
  const customerEmail = customerEmailProp || reviewData?.[0]?.customer?.email || ''
  const customerPhone = reviewData?.[0]?.customer?.phone || ''
  const isCourierCustomer = reviewData?.[0]?.customer?.transmission === 'courier'

  // Task 12: Enhanced functionality state
  const [selectedTransactions, setSelectedTransactions] = useState(new Set())
  // Per-line edits — unified model for price overrides AND splits.
  // { [saleItemId]: { split: boolean, reason?: string, lines: [{ splitId, quantity, unitPrice, priceTierId|null }] } }
  // Unsplit override = 1 line (quantity locked to original); split = 2 lines sharing the locked kg total.
  // An item with no entry invoices at its original price.
  const [lineEdits, setLineEdits] = useState({})
  // Pricing catalogue loaded once when the modal opens — powers inline tier /
  // volume-discount dropdowns in the transactions table.
  const [productPrices, setProductPrices] = useState([])
  const [volumeDiscounts, setVolumeDiscounts] = useState([])

  // Discount state
  const [discountType, setDiscountType] = useState('percentage')
  const [discountAmount, setDiscountAmount] = useState(0)
  const [discountReason, setDiscountReason] = useState('')

  // Shipping charges state (courier customers only — multiple charges supported)
  const [shippingCharges, setShippingCharges] = useState([])

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('Xero (default)')

  // GoCardless customer mapping state
  const [isGoCardlessCustomer, setIsGoCardlessCustomer] = useState(false)

  const [isCreating, setIsCreating] = useState(false)

  // Error dialog state
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorDialogTitle, setErrorDialogTitle] = useState('Error')
  const [errorDialogMessage, setErrorDialogMessage] = useState('')
  const [errorDialogRetryable, setErrorDialogRetryable] = useState(false)
  const [errorDialogFullError, setErrorDialogFullError] = useState(null)
  const [errorCopied, setErrorCopied] = useState(false)

  // Success dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [successDialogMessage, setSuccessDialogMessage] = useState('')

  // Credit note check state
  const [creditNoteInfo, setCreditNoteInfo] = useState(null) // { totalCredit, creditNotes }
  const [checkingCredits, setCheckingCredits] = useState(false)

  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [progressSteps, setProgressSteps] = useState([])
  const [progressPercent, setProgressPercent] = useState(0)

  // Helper function to show error dialog with human-readable messages
  const showError = (error) => {
    console.error('Invoice creation error - Full error object:', error)
    console.error('Error data:', error.data)
    console.error('Error message:', error.message)

    // Extract error details from response
    let title = 'Invoice Creation Failed'
    let message = error.message || 'An unexpected error occurred. Please try again.'
    let retryable = true

    // Check if this is a structured error from the backend
    if (error.data) {
      // Backend returns { error: "CODE", message: "Human message", retryable: bool }
      message = error.data.message || error.data.error || message
      retryable = error.data.retryable !== false

      console.error('Parsed backend error:', {
        code: error.data.error,
        message: error.data.message,
        retryable: error.data.retryable
      })

      // Customize title based on error code
      if (error.data.error === 'XERO_NOT_CONNECTED') {
        title = 'Xero Not Connected'
      } else if (error.data.error === 'SUPABASE_AUTH_EXPIRED') {
        title = 'Session Expired'
      } else if (error.data.error === 'XERO_AUTH_EXPIRED' || error.data.error === 'AUTH_EXPIRED') {
        title = 'Xero Connection Expired'
      } else if (error.data.error === 'CUSTOMER_NOT_MAPPED') {
        title = 'Customer Not Mapped'
      } else if (error.data.error === 'RATE_LIMIT_EXCEEDED') {
        title = 'Rate Limit Reached'
      } else if (error.data.error === 'XERO_VALIDATION_ERROR') {
        title = 'Validation Error'
      } else if (error.data.error === 'TIMEOUT_ERROR') {
        title = 'Request Timed Out'
      } else if (error.data.error === 'XERO_API_ERROR') {
        title = 'Xero API Error'
      } else if (error.data.error === 'UNKNOWN_ERROR') {
        title = 'Unexpected Error'
      }
    }

    console.error('Final error display:', { title, message, retryable })

    setErrorDialogTitle(title)
    setErrorDialogMessage(message)
    setErrorDialogRetryable(retryable)
    setErrorDialogFullError(error) // Store full error for copying
    setErrorCopied(false) // Reset copied state
    setShowErrorDialog(true)
  }

  // Helper function to copy error details to clipboard
  const copyErrorToClipboard = async () => {
    try {
      const errorDetails = {
        title: errorDialogTitle,
        message: errorDialogMessage,
        timestamp: new Date().toISOString(),
        errorCode: errorDialogFullError?.data?.error || 'UNKNOWN',
        retryable: errorDialogRetryable,
        fullError: {
          message: errorDialogFullError?.message,
          data: errorDialogFullError?.data,
          stack: errorDialogFullError?.stack
        }
      }

      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2))
      setErrorCopied(true)

      // Reset copied state after 2 seconds
      setTimeout(() => setErrorCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy error to clipboard:', err)
    }
  }

  // Draft preview state
  const [draftInvoice, setDraftInvoice] = useState(null)
  const [showDraftPreview, setShowDraftPreview] = useState(false)
  const [includeStatement, setIncludeStatement] = useState(false)
  const [statementType, setStatementType] = useState('since_zero')
  const [isReconciling, setIsReconciling] = useState(false)
  const [outstandingCount, setOutstandingCount] = useState(null)

  // Helper function to check if email is required for the selected payment method
  const isEmailRequiredForPayment = useCallback((method) => {
    // Only Xero and GoCardless require email
    // Cash does not require email
    return method === 'Xero (default)' || method === 'GoCardless Direct Debit'
  }, [])

  // Task 5.1: Check customer mapping status when modal opens
  useEffect(() => {
    async function checkMapping() {
      if (isOpen && customerId) {
        setCheckingMapping(true)
        try {
          const xeroMapped = await isCustomerMappedToXero(customerId)
          const goCardlessMapped = await isCustomerMappedToGoCardless(customerId)
          setIsCustomerMapped(xeroMapped)
          setIsGoCardlessCustomer(goCardlessMapped)
          // Auto-select GoCardless if the customer has an active mandate
          if (goCardlessMapped) {
            setPaymentMethod('GoCardless Direct Debit')
          }
        } catch (error) {
          console.error('Error checking customer mapping:', error)
          setIsCustomerMapped(false)
          setIsGoCardlessCustomer(false)
        } finally {
          setCheckingMapping(false)
        }
      }
    }
    checkMapping()
  }, [isOpen, customerId])

  // Check for outstanding credit notes when modal opens
  useEffect(() => {
    async function checkCreditNotes() {
      if (isOpen && customerId && isCustomerMapped) {
        setCheckingCredits(true)
        try {
          // Get customer's xero_contact_id
          const { data: customer } = await supabase
            .from('customers')
            .select('xero_contact_id')
            .eq('id', customerId)
            .single()

          if (customer?.xero_contact_id) {
            const { data, error } = await supabase.functions.invoke('xero-invoices', {
              body: { action: 'check_credit_notes', contactId: customer.xero_contact_id }
            })
            if (!error && data?.success && data.totalCredit > 0) {
              setCreditNoteInfo({ totalCredit: data.totalCredit, creditNotes: data.creditNotes })
            } else {
              setCreditNoteInfo(null)
            }
          }
        } catch (err) {
          console.error('Error checking credit notes:', err)
          setCreditNoteInfo(null)
        } finally {
          setCheckingCredits(false)
        }
      } else {
        setCreditNoteInfo(null)
      }
    }
    checkCreditNotes()
  }, [isOpen, customerId, isCustomerMapped])

  // Initialize selected transactions when modal opens
  useEffect(() => {
    if (isOpen && reviewData) {
      const allSaleItemIds = new Set()
      reviewData.forEach(sale => {
        sale.sale_items.forEach(item => {
          allSaleItemIds.add(item.id)
        })
      })
      setSelectedTransactions(allSaleItemIds)
      setLineEdits({})
      setDiscountAmount(0)
      setDiscountReason('')
      setShippingCharges([])
      setPaymentMethod('Xero (default)')
    }
  }, [isOpen, reviewData])

  // Task 5.1: Handler for mapping completion
  const handleMappingComplete = async () => {
    // Recheck mapping status after successful mapping
    if (customerId) {
      try {
        const mapped = await isCustomerMappedToXero(customerId)
        setIsCustomerMapped(mapped)
      } catch (error) {
        console.error('Error rechecking mapping:', error)
      }
    }
  }

  // Task 5.1: Handler for button click - either map or send to Xero
  const handleButtonClick = () => {
    if (!isCustomerMapped) {
      setShowMappingModal(true)
    } else {
      onGenerateInvoice()
    }
  }

  // Toggle individual transaction
  const toggleTransaction = (saleItemId) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(saleItemId)) {
        newSet.delete(saleItemId)
      } else {
        newSet.add(saleItemId)
      }
      return newSet
    })
  }

  // Toggle all transactions
  const toggleAllTransactions = () => {
    if (!reviewData) return
    const allSaleItemIds = new Set()
    reviewData.forEach(sale => {
      sale.sale_items.forEach(item => {
        allSaleItemIds.add(item.id)
      })
    })

    if (selectedTransactions.size === allSaleItemIds.size) {
      setSelectedTransactions(new Set())
    } else {
      setSelectedTransactions(allSaleItemIds)
    }
  }

  // ── Line item editing (price override + split) ────────────────────────────
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
  const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi)

  // Splits are only offered for fresh kg products (block formats / growing
  // materials use non-kg units, so a kg split is meaningless in v1).
  const itemCanSplit = (item) => !!item && !item.block_format_id && !item.growing_material_id

  // The effective per-kg price of a line as currently invoiced (folds in any
  // existing promotional discount), used to seed the editor so opening + saving
  // an untouched line leaves the total unchanged.
  const seedUnitPrice = (item) => {
    const qty = parseFloat(item.quantity) || 0
    if (qty > 0 && item.sub_total != null) return round2(parseFloat(item.sub_total) / qty)
    return round2(parseFloat(item.price) || 0)
  }

  // Total money for an item given current edits (split sum, override, or original).
  const lineItemTotal = (item) => {
    const e = lineEdits[item.id]
    if (!e) return parseFloat(item.sub_total)
    return e.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0), 0)
  }

  const splitBreakdown = (item) => {
    const e = lineEdits[item.id]
    if (!e?.split) return null
    return e.lines.map(l => `${round2(l.quantity)}kg @ £${(parseFloat(l.unitPrice) || 0).toFixed(2)}`).join(' · ')
  }

  // Pricing options (tier rates + volume-discount breaks) available for a line's
  // product+grade, built once from the catalogue loaded when the modal opens.
  const getPricingOptions = (item) => {
    if (!item?.product_id || !item?.product_type_id) return []
    const tiers = productPrices
      .filter(r => r.product_id === item.product_id && r.product_type_id === item.product_type_id)
      .sort((a, b) => (a.price_tier?.sort_order ?? 0) - (b.price_tier?.sort_order ?? 0))
    // "Current" entry represents the line's original realised price (which may be a
    // promo/negotiated rate that differs from the catalogue tier price). Keeps the
    // dropdown selection honest and gives a way to revert a line to its original rate.
    const appliedName = item.price_tier?.display_name || item.price_tier?.name || 'Current'
    const opts = [{ key: 'orig', tierId: item.applied_price_tier_id || null, label: appliedName, unitPrice: seedUnitPrice(item), isVolume: false, minQty: 0, isOriginal: true }]
    for (const t of tiers) {
      const tierName = t.price_tier?.display_name || t.price_tier?.name || 'Tier'
      const base = round2(parseFloat(t.price_per_kg))
      opts.push({ key: `tier:${t.price_tier_id}`, tierId: t.price_tier_id, label: tierName, unitPrice: base, isVolume: false, minQty: 0 })
      // Volume-discount breaks for this tier (product-specific or global), where available
      volumeDiscounts
        .filter(v => v.active && v.price_tier_id === t.price_tier_id && (v.product_id === item.product_id || v.product_id == null))
        .sort((a, b) => parseFloat(a.min_quantity) - parseFloat(b.min_quantity))
        .forEach(v => {
          opts.push({
            key: `vol:${t.price_tier_id}:${v.min_quantity}`,
            tierId: t.price_tier_id,
            label: `${tierName} ${parseFloat(v.min_quantity)}kg+`,
            unitPrice: round2(base * (1 - parseFloat(v.discount_percent) / 100)),
            isVolume: true,
            minQty: parseFloat(v.min_quantity)
          })
        })
    }
    return opts
  }

  // The pricing option a line currently maps to (for the dropdown's selected value).
  // Falls back to matching the line's actual unit price, then to "Current".
  const lineSelectedOptionKey = (item, line) => {
    if (line?.priceOptionKey) return line.priceOptionKey
    const opts = getPricingOptions(item)
    if (opts.length === 0) return undefined
    const up = line != null ? (parseFloat(line.unitPrice) || 0) : seedUnitPrice(item)
    const match = opts.find(o => Math.abs(o.unitPrice - up) < 0.005)
    return match ? match.key : 'orig'
  }

  // Short human label of the rate a line uses, for the Xero/customer description
  // (e.g. "100kg+ rate", "Commercial rate"). null = original/standard or custom.
  const lineRateNote = (item, line) => {
    if (!line?.priceOptionKey) return null
    const opt = getPricingOptions(item).find(o => o.key === line.priceOptionKey)
    if (!opt || opt.isOriginal) return null
    return opt.isVolume ? `${opt.minQty}kg+ rate` : `${opt.label} rate`
  }

  // Mutate one line of an item's edit in place
  const patchEditLine = (itemId, index, patch) => {
    setLineEdits(prev => {
      const e = prev[itemId]
      if (!e) return prev
      const lines = e.lines.map((l, i) => i === index ? { ...l, ...patch } : l)
      return { ...prev, [itemId]: { ...e, lines } }
    })
  }

  // Apply a chosen pricing option (tier / volume break) to a line
  const selectLinePrice = (item, index, optKey) => {
    const opt = getPricingOptions(item).find(o => o.key === optKey)
    if (!opt) return
    if (!lineEdits[item.id]) {
      // First edit on an unsplit line — picking the original rate is a no-op
      if (opt.isOriginal) return
      const full = round2(parseFloat(item.quantity))
      setLineEdits(prev => ({
        ...prev,
        [item.id]: { split: false, lines: [{ splitId: crypto.randomUUID(), quantity: full, unitPrice: opt.unitPrice, priceTierId: opt.tierId, priceOptionKey: opt.key }] }
      }))
      return
    }
    // Reverting the only line of an unsplit override back to "Current" clears the edit
    const e = lineEdits[item.id]
    if (opt.isOriginal && !e.split) {
      clearLineEdit(item.id)
      return
    }
    patchEditLine(item.id, index, { unitPrice: opt.unitPrice, priceTierId: opt.tierId, priceOptionKey: opt.key })
  }

  // Manual unit-price entry clears the tier/volume selection
  const setLineUnitPrice = (item, index, value) => {
    const up = value != null ? parseFloat(value) : 0
    if (!lineEdits[item.id]) {
      const full = round2(parseFloat(item.quantity))
      setLineEdits(prev => ({
        ...prev,
        [item.id]: { split: false, lines: [{ splitId: crypto.randomUUID(), quantity: full, unitPrice: isNaN(up) ? 0 : up, priceTierId: null, priceOptionKey: null }] }
      }))
      return
    }
    patchEditLine(item.id, index, { unitPrice: isNaN(up) ? 0 : up, priceTierId: null, priceOptionKey: null })
  }

  // Split a line into two sub-lines sharing the locked kg total
  const startSplit = (item) => {
    const full = round2(parseFloat(item.quantity))
    const existing = lineEdits[item.id]?.lines?.[0]
    const unitPrice = parseFloat(existing?.unitPrice) || seedUnitPrice(item)
    const priceTierId = existing?.priceTierId ?? item.applied_price_tier_id ?? null
    const priceOptionKey = existing?.priceOptionKey ?? 'orig'
    setLineEdits(prev => ({
      ...prev,
      [item.id]: {
        split: true,
        lines: [
          { splitId: crypto.randomUUID(), quantity: full, unitPrice, priceTierId, priceOptionKey },
          { splitId: crypto.randomUUID(), quantity: 0, unitPrice, priceTierId, priceOptionKey }
        ]
      }
    }))
  }

  // Two-line redistribution: editing one quantity forces the other so they
  // always sum to the locked kg total.
  const setSplitQty = (item, index, value) => {
    const lockedKg = round2(parseFloat(item.quantity))
    const raw = value != null ? parseFloat(value) : 0
    const v = clamp(isNaN(raw) ? 0 : raw, 0, lockedKg)
    const j = index === 0 ? 1 : 0
    setLineEdits(prev => {
      const e = prev[item.id]
      if (!e) return prev
      const lines = e.lines.map(l => ({ ...l }))
      lines[index].quantity = round2(v)
      if (lines[j]) lines[j].quantity = round2(lockedKg - v)
      return { ...prev, [item.id]: { ...e, lines } }
    })
  }

  // Remove any edit (split or override) and revert to the original price
  const clearLineEdit = (itemId) => {
    setLineEdits(prev => {
      const n = { ...prev }
      delete n[itemId]
      return n
    })
  }

  // Load the pricing catalogue (per-tier prices + volume discounts) once when the
  // review modal opens, so inline tier dropdowns can offer bulk / volume rates.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    Promise.all([fetchProductPrices(), fetchVolumeDiscounts()])
      .then(([prices, discounts]) => {
        if (cancelled) return
        setProductPrices(prices || [])
        setVolumeDiscounts(discounts || [])
      })
      .catch(err => {
        if (cancelled) return
        console.error('[ReviewModal] Failed to load pricing catalogue', err)
        setProductPrices([])
        setVolumeDiscounts([])
      })
    return () => { cancelled = true }
  }, [isOpen])

  // Shipping charge handlers
  const addShippingCharge = () => setShippingCharges(prev => [...prev, null])
  const updateShippingCharge = (index, value) => setShippingCharges(prev => prev.map((v, i) => i === index ? value : v))
  const removeShippingCharge = (index) => setShippingCharges(prev => prev.filter((_, i) => i !== index))

  // Calculate totals in real-time
  const calculateTotals = () => {
    if (!reviewData) {
      return { subtotal: '0.00', discount: '0.00', shippingChargesTotal: '0.00', total: '0.00' }
    }

    // Sum selected transactions with per-line edits (override/split) applied
    let subtotal = 0
    reviewData.forEach(sale => {
      sale.sale_items.forEach(item => {
        if (selectedTransactions.has(item.id)) {
          subtotal += lineItemTotal(item)
        }
      })
    })

    // Calculate discount
    let discountValue = 0
    if (discountAmount > 0) {
      if (discountType === 'percentage') {
        discountValue = (subtotal * discountAmount) / 100
      } else {
        discountValue = discountAmount
      }
    }

    // Add shipping charges (credit notes are applied via Xero allocation after authorisation)
    const shippingTotal = shippingCharges.reduce((sum, v) => sum + (v || 0), 0)
    const total = subtotal - discountValue + shippingTotal

    return {
      subtotal: subtotal.toFixed(2),
      discount: discountValue.toFixed(2),
      shippingChargesTotal: shippingTotal.toFixed(2),
      total: Math.max(0, total).toFixed(2) // Prevent negative totals
    }
  }

  const totals = calculateTotals()

  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    })
  }

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`
  }

  const formatProductName = (item, { mobile } = {}) => {
    // Block item
    if (item.block_format_id && item.block_format) {
      const strain = item.product?.strain || 'Unknown'
      if (mobile) {
        // "Whitby Oyster 10B" — strip supplier prefix, units/weight shown on second line
        const formatCode = item.block_format.name.replace(/^Satrise\s+/i, '')
        return `${strain} - ${formatCode}`
      }
      return `${strain} Fruiting Block (${item.block_format.name})`
    }

    // Growing-material item
    if (item.growing_material_id && item.growing_material) {
      return item.growing_material.name || 'Growing Material'
    }

    const strain = item.product?.strain
    const type = item.product_type?.name

    if (strain && type) {
      return `${strain} - ${type}`
    }
    return strain || type || 'Unknown Product'
  }

  const handleDisregardClick = (sale) => {
    // Close the Dialog first to avoid focus trap conflict
    setSaleToDisregard(sale)
    setPendingDisregard(true)
    onClose()
  }

  const handleConfirmDisregard = () => {
    if (saleToDisregard && onDisregardSale) {
      onDisregardSale(saleToDisregard.id)
    }
    setShowDisregardDialog(false)
    setSaleToDisregard(null)
    setPendingDisregard(false)
  }

  const handleDisregardCancel = () => {
    setShowDisregardDialog(false)
    setSaleToDisregard(null)
    setPendingDisregard(false)
  }

  // Clear draft preview and return to form
  const clearDraftPreview = () => {
    setDraftInvoice(null)
    setShowDraftPreview(false)
    setIncludeStatement(false)
    setStatementType('since_zero')
    setOutstandingCount(null)
  }

  // Monitor when Dialog closes and pendingDisregard is true, then show AlertDialog
  useEffect(() => {
    if (pendingDisregard && !isOpen) {
      // Small delay to ensure Dialog is fully closed
      const timer = setTimeout(() => {
        setShowDisregardDialog(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [pendingDisregard, isOpen])

  // Fetch outstanding invoice count when draft preview opens
  useEffect(() => {
    if (!showDraftPreview || !customerId) return

    const fetchOutstandingCount = async () => {
      try {
        const { count, error } = await supabase
          .from('invoices')
          .select('*', { count: 'exact', head: true })
          .eq('customer_id', customerId)
          .eq('paid', false)
          .neq('status', 'voided')
          .in('xero_invoice_status', ['authorised', 'submitted'])

        if (!error) setOutstandingCount(count || 0)
      } catch (err) {
        console.error('Error fetching outstanding count:', err)
      }
    }

    fetchOutstandingCount()
  }, [showDraftPreview, customerId])

  // Group transactions by branch
  const transactionsByBranch = React.useMemo(() => {
    if (!reviewData) return {}

    const grouped = {}
    reviewData.forEach(sale => {
      const branchId = sale.branch_id || 'main'
      const branchName = sale.branch?.name || 'Main Branch'

      if (!grouped[branchId]) {
        grouped[branchId] = {
          branchId,
          branchName,
          transactions: []
        }
      }

      sale.sale_items.forEach(item => {
        grouped[branchId].transactions.push({
          ...item,
          saleDate: sale.date,
          saleId: sale.id,
          salesIdNumber: sale.sales_id
        })
      })
    })

    return grouped
  }, [reviewData])

  // Calculate branch totals
  const branchTotals = React.useMemo(() => {
    const totals = {}
    Object.entries(transactionsByBranch).forEach(([branchId, data]) => {
      totals[branchId] = data.transactions.reduce((sum, item) => {
        if (selectedTransactions.has(item.id)) {
          const e = lineEdits[item.id]
          const amount = e
            ? e.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0), 0)
            : parseFloat(item.sub_total)
          return sum + amount
        }
        return sum
      }, 0)
    })
    return totals
  }, [transactionsByBranch, selectedTransactions, lineEdits])

  // Toggle all transactions for a branch
  const toggleBranch = (branchId) => {
    const branchTransactions = transactionsByBranch[branchId].transactions
    const branchTransactionIds = branchTransactions.map(t => t.id)
    const allSelected = branchTransactionIds.every(id => selectedTransactions.has(id))

    setSelectedTransactions(prev => {
      const newSet = new Set(prev)
      if (allSelected) {
        // Deselect all
        branchTransactionIds.forEach(id => newSet.delete(id))
      } else {
        // Select all
        branchTransactionIds.forEach(id => newSet.add(id))
      }
      return newSet
    })
  }

  // Get checkbox state for branch
  const getBranchCheckboxState = (branchId) => {
    const branchTransactions = transactionsByBranch[branchId]?.transactions || []
    const branchTransactionIds = branchTransactions.map(t => t.id)
    const selectedCount = branchTransactionIds.filter(id => selectedTransactions.has(id)).length

    return {
      checked: selectedCount === branchTransactionIds.length && selectedCount > 0,
      indeterminate: selectedCount > 0 && selectedCount < branchTransactionIds.length
    }
  }

  // Build the create_draft line payload from the current review + per-line edits.
  // line_items is the source of truth for what reaches Xero (one Xero line per
  // entry); an edited item expands into its lines (1 for an override, 2 for a
  // split), dropping the promotional discount since the unit price is now
  // authoritative. line_item_splits / line_item_adjustments carry the audit
  // breakdown into invoices.metadata.
  const buildLinePayload = () => {
    const lineItems = reviewData
      .flatMap(sale =>
        sale.sale_items.flatMap(item => {
          const productName = formatProductName(item)
          const base = {
            sale_item_id: String(item.id),
            sale_id: sale.id,
            product_name: productName,
            tray_number: item.tray_number || null
          }
          const e = lineEdits[item.id]
          if (e) {
            return e.lines.map(l => {
              // Annotate the line with the rate applied so the customer's invoice
              // reads e.g. "Shiitake - A Class (100kg+ rate)".
              const note = lineRateNote(item, l)
              return {
                ...base,
                product_name: note ? `${productName} (${note})` : productName,
                quantity: round2(parseFloat(l.quantity) || 0),
                price: round2(parseFloat(l.unitPrice) || 0)
              }
            })
          }
          const discount = parseFloat(item.discount_percent || 0)
          return [{
            ...base,
            quantity: item.quantity,
            price: parseFloat(item.price),
            discount_rate: discount > 0 ? discount : undefined,
            discount_note: item.discount_note || undefined
          }]
        })
      )
      .filter(li => selectedTransactions.has(li.sale_item_id))

    const lineItemSplits = Object.entries(lineEdits)
      .filter(([, e]) => e.split)
      .map(([saleItemId, e]) => ({
        sale_item_id: parseInt(saleItemId),
        sub_lines: e.lines.map(l => ({
          quantity: round2(parseFloat(l.quantity) || 0),
          unit_price: round2(parseFloat(l.unitPrice) || 0),
          price_tier_id: l.priceTierId || null
        }))
      }))

    const lineItemAdjustments = Object.entries(lineEdits)
      .filter(([, e]) => !e.split)
      .map(([saleItemId, e]) => ({
        sale_item_id: parseInt(saleItemId),
        adjusted_amount: round2(e.lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0), 0))
      }))

    return { lineItems, lineItemSplits, lineItemAdjustments }
  }

  const handleCreateAndSend = async () => {
    setIsCreating(true)
    const steps = [
      { label: 'Creating draft invoice...', status: 'active', detail: null },
      { label: 'Authorising & sending invoice...', status: 'pending', detail: null },
      { label: 'Checking for credit notes...', status: 'pending', detail: null },
      { label: 'Done', status: 'pending', detail: null }
    ]
    setProgressSteps([...steps])
    setProgressPercent(10)
    setShowProgressModal(true)

    const updateStep = (index, status, detail = null) => {
      steps[index] = { ...steps[index], status, detail }
      setProgressSteps([...steps])
    }

    try {
      const paymentMethodValue = paymentMethod === 'Xero (default)' ? 'xero'
        : paymentMethod === 'GoCardless Direct Debit' ? 'gocardless'
        : paymentMethod === 'Cash' ? 'cash'
        : 'xero'

      const { lineItems, lineItemSplits, lineItemAdjustments } = buildLinePayload()

      // Step 1: Create draft
      setProgressPercent(20)
      const { data: draftData, error: draftError } = await retryXeroApiCall(
        async () => {
          const { data: responseData, error: responseError } = await supabase.functions.invoke('xero-invoices', {
            body: {
              action: 'create_draft',
              customer_id: customerId,
              branch_id: selectedBranchIds.length === 1 && selectedBranchIds[0] !== 'main'
                ? selectedBranchIds[0]
                : null,
              payment_method: paymentMethodValue,
              discount: discountAmount > 0 ? { type: discountType, amount: discountAmount, reason: discountReason || undefined } : null,
              shipping_charges: shippingCharges.filter(v => v && v > 0).length > 0 ? shippingCharges.filter(v => v && v > 0) : null,
              excluded_transactions: Array.from(reviewData.flatMap(s => s.sale_items.map(i => i.id))).filter(id => !selectedTransactions.has(id)),
              line_item_adjustments: lineItemAdjustments,
              line_item_splits: lineItemSplits,
              line_items: lineItems
            }
          })
          if (responseError) {
            const err = new Error(responseError.message || 'Failed to create draft invoice')
            err.status = 500
            err.retryable = true
            throw err
          }
          if (responseData && !responseData.success) {
            const err = new Error(responseData.message || responseData.error || 'Failed to create draft invoice')
            err.data = responseData
            err.status = 500
            err.retryable = responseData.retryable !== false
            throw err
          }
          return { data: responseData, error: null }
        },
        {
          onRetry: (error, attempt, delay) => {
            console.log(`Retrying Create Draft (attempt ${attempt}) in ${delay}ms:`, error.message)
          }
        }
      )

      if (draftError || !draftData?.success) {
        throw draftError || new Error('Failed to create draft invoice')
      }

      updateStep(0, 'done', 'Draft created')
      setProgressPercent(40)

      // Step 2: Authorise and send
      updateStep(1, 'active')
      const { data: sendData, error: sendError } = await supabase.functions.invoke('xero-invoices', {
        body: {
          action: 'authorise-and-send',
          invoice_id: draftData.invoice_id,
          xero_invoice_id: draftData.xero_invoice_id,
          email_to: customerEmail
        }
      })

      if (sendError) {
        const err = new Error(sendError.message || 'Failed to send invoice')
        err.status = 500
        err.retryable = true
        throw err
      }
      if (sendData && !sendData.success) {
        const err = new Error(sendData.message || sendData.error || 'Failed to send invoice')
        err.data = sendData
        err.status = 500
        err.retryable = sendData.retryable !== false
        throw err
      }

      updateStep(1, 'done', 'Invoice sent')
      setProgressPercent(65)

      // Step 3: Credit notes
      updateStep(2, 'active')
      if (sendData.credit_applied) {
        updateStep(2, 'done', `£${sendData.credit_total_applied?.toFixed(2)} credit applied`)
      } else {
        updateStep(2, 'done', 'No credit notes')
      }
      setProgressPercent(85)

      // Step 4: Done
      updateStep(3, 'done')
      setProgressPercent(100)

      let successMessage = 'Invoice created and sent successfully'

      if (paymentMethodValue === 'gocardless' && sendData.payment_automation_triggered) {
        successMessage = '✓ Invoice created and sent\n✓ GoCardless payment automation triggered'
      } else if (paymentMethodValue === 'xero' && sendData.online_payment_url) {
        successMessage = `✓ Invoice created and sent\n✓ Online payment URL: ${sendData.online_payment_url}\n\n(URL copied to clipboard)`
        navigator.clipboard.writeText(sendData.online_payment_url).catch(() => {})
      } else if (paymentMethodValue === 'cash') {
        successMessage = sendData.cash_payment_recorded
          ? '✓ Invoice created and sent\n✓ Cash payment recorded — £0.00 due'
          : '✓ Invoice created and sent\n⚠ Cash payment failed — check Xero'
      } else if (paymentMethodValue === 'gocardless' && !sendData.payment_automation_triggered) {
        successMessage = '✓ Invoice created and sent\n⚠ Payment automation failed - invoice created but payment not initiated'
      }

      if (sendData.credit_applied && sendData.credit_total_applied > 0) {
        successMessage += `\n✓ Credit applied: £${sendData.credit_total_applied.toFixed(2)}`
        successMessage += `\n  Amount due: £${sendData.credit_remaining_due?.toFixed(2) || '0.00'}`
        if (sendData.credit_remaining_on_account > 0) {
          successMessage += `\n  Remaining credit on account: £${sendData.credit_remaining_on_account.toFixed(2)}`
        }
      }

      await new Promise(r => setTimeout(r, 1000))
      setShowProgressModal(false)
      setSuccessDialogMessage(successMessage)
      setShowSuccessDialog(true)
      onClose()
      onDataRefresh()
    } catch (error) {
      setShowProgressModal(false)
      showError(error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateDraft = async () => {
    setIsCreating(true)
    try {
      const paymentMethodValue = paymentMethod === 'Xero (default)' ? 'xero'
        : paymentMethod === 'GoCardless Direct Debit' ? 'gocardless'
        : paymentMethod === 'Cash' ? 'cash'
        : 'xero'

      const { lineItems, lineItemSplits, lineItemAdjustments } = buildLinePayload()

      const { data, error } = await retryXeroApiCall(
        async () => {
          const { data: responseData, error: responseError } = await supabase.functions.invoke('xero-invoices', {
            body: {
              action: 'create_draft',
              customer_id: customerId,
              branch_id: selectedBranchIds.length === 1 && selectedBranchIds[0] !== 'main'
                ? selectedBranchIds[0]
                : null,
              payment_method: paymentMethodValue,
              discount: discountAmount > 0 ? {
                type: discountType,
                amount: discountAmount
              } : null,
              shipping_charges: shippingCharges.filter(v => v && v > 0).length > 0 ? shippingCharges.filter(v => v && v > 0) : null,
              excluded_transactions: Array.from(reviewData.flatMap(s => s.sale_items.map(i => i.id))).filter(id => !selectedTransactions.has(id)),
              line_item_adjustments: lineItemAdjustments,
              line_item_splits: lineItemSplits,
              line_items: lineItems
            }
          })

          if (responseError) {
            const err = new Error(responseError.message || 'Network error connecting to Xero')
            err.status = responseError.status || 500
            err.retryable = true
            throw err
          }

          if (responseData && !responseData.success) {
            const err = new Error(responseData.message || responseData.error || 'Failed to create draft invoice')
            err.data = responseData
            err.status = 200
            err.retryable = responseData.retryable !== false
            throw err
          }

          return { data: responseData, error: null }
        },
        {
          onRetry: (error, attempt, delay) => {
            console.log(`Retrying Create Draft (attempt ${attempt}) in ${delay}ms:`, error.message)
          }
        }
      )

      if (error || !data?.success) {
        throw error || new Error('Failed to create draft invoice')
      }

      // Trigger async PDF fetch (fire-and-forget)
      supabase.functions.invoke('xero-invoices', {
        body: {
          action: 'fetch-pdf',
          invoiceId: data.invoice_id,
          xeroInvoiceId: data.xero_invoice_id
        }
      }).catch(pdfError => {
        console.warn('[PDF Fetch] Background fetch failed:', pdfError)
      })

      await Promise.all([
        onDraftCreated?.(),
        onUninvoicedDataRefresh?.()
      ])

      setDraftInvoice(data)
      setShowDraftPreview(true)
    } catch (error) {
      showError(error)
    } finally {
      setIsCreating(false)
    }
  }

  const handleMarkAsPaid = async (e) => {
    if (e) e.preventDefault()
    setIsMarkingPaid(true)
    try {
      const selectedSaleIds = [...new Set(
        reviewData
          .filter(sale => sale.sale_items.some(item => selectedTransactions.has(item.id)))
          .map(sale => sale.id)
      )]
      await onMarkAsPaid(selectedSaleIds)
      setShowCashConfirm(false)
    } catch (error) {
      showError(error)
    } finally {
      setIsMarkingPaid(false)
    }
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        clearDraftPreview()
        onClose()
      }
    }}>
      <DialogContent className="odin-modal-large p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Review Accruing Transactions</DialogTitle>
        </DialogHeader>

        {!showDraftPreview ? (
          <div className="space-y-4">
            {/* Customer info */}
            <div
              className="cursor-pointer rounded-md p-2 -m-2 hover:bg-muted/50 transition-colors"
              onClick={() => onCustomerClick?.({ id: customerId, name: customerName })}
            >
              <Label className="text-sm font-medium text-muted-foreground">Customer</Label>
              <div className="flex items-center gap-3 mt-1">
                <p className="font-semibold">{customerName}</p>
                {reviewData?.[0]?.customer?.payment_terms && (
                  <Badge variant="outline" className="text-xs font-normal">
                    {reviewData[0].customer.payment_terms} day terms
                  </Badge>
                )}
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>

          {/* Transaction Table - Grouped by Branch */}
          <div className="border-t pt-4">
            {Object.entries(transactionsByBranch).map(([branchId, branchData]) => {
              const checkboxState = getBranchCheckboxState(branchId)
              const branchTotal = branchTotals[branchId] || 0

              return (
                <div key={branchId} className="mb-6 last:mb-0">
                  {/* Branch Header */}
                  <div
                    className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleBranch(branchId)}
                    role="group"
                    aria-label={`${branchData.branchName} transactions`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={checkboxState.checked}
                        indeterminate={checkboxState.indeterminate}
                        onCheckedChange={() => toggleBranch(branchId)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select all ${branchData.branchName} transactions`}
                      />
                      <span className="font-medium text-gray-900">{branchData.branchName}</span>
                    </div>
                    <span className="font-mono font-semibold text-gray-900" aria-label={`${branchData.branchName} total: £${branchTotal.toFixed(2)}`}>
                      £{branchTotal.toFixed(2)}
                    </span>
                  </div>

                  {/* Branch Transactions — Desktop Table */}
                  <div className="hidden sm:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Sales ID</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Weight</TableHead>
                        <TableHead>Unit Price</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {branchData.transactions.map(item => {
                        const isSelected = selectedTransactions.has(item.id)
                        const edit = lineEdits[item.id]
                        const isEdited = !!edit
                        const isSplitItem = !!edit?.split
                        const canSplit = itemCanSplit(item)
                        const discount = parseFloat(item.discount_percent || 0)
                        const basePrice = parseFloat(item.price)
                        const effectiveUnitPrice = basePrice * (1 - discount / 100)
                        const hasDiscount = discount > 0
                        const lockedKg = round2(parseFloat(item.quantity))
                        const options = getPricingOptions(item)
                        const grade = item.product_type?.name || '—'

                        const weightLabel = item.block_format_id && item.block_format
                          ? `${parseFloat(item.quantity)} Unit${parseFloat(item.quantity) !== 1 ? 's' : ''} (${(parseFloat(item.quantity) * (parseFloat(item.block_format.weight_kg) || 0)).toFixed(1)}kg)`
                          : item.growing_material_id
                          ? `${parseFloat(item.quantity)} ${item.growing_material?.unit_label || 'unit'}${parseFloat(item.quantity) !== 1 ? 's' : ''}`
                          : `${item.quantity}kg`

                        // Tier dropdown (or static text when no catalogue options) for a given line
                        const renderTierCell = (line, index) => {
                          if (options.length === 0) {
                            return <span className="text-sm">{item.price_tier?.display_name || item.price_tier?.name || '—'}</span>
                          }
                          return (
                            <Select value={lineSelectedOptionKey(item, line)} onValueChange={(k) => selectLinePrice(item, index, k)}>
                              <SelectTrigger className="h-8 w-[170px]">
                                <SelectValue placeholder="Select tier" />
                              </SelectTrigger>
                              <SelectContent>
                                {options.map(o => (
                                  <SelectItem key={o.key} value={o.key}>
                                    {o.label} — £{o.unitPrice.toFixed(2)}/kg
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )
                        }

                        if (isSplitItem) {
                          // Two sub-rows sharing the locked kg total
                          return (
                            <React.Fragment key={item.id}>
                              {edit.lines.map((line, index) => {
                                const lineTotal = round2((parseFloat(line.quantity) || 0) * (parseFloat(line.unitPrice) || 0))
                                const isFirst = index === 0
                                return (
                                  <TableRow
                                    key={line.splitId}
                                    className={`bg-yellow-50 ${!isSelected ? 'opacity-50' : ''} ${!isFirst ? 'border-t-0' : ''}`}
                                  >
                                    <TableCell>
                                      {isFirst && (
                                        <Checkbox checked={isSelected} onCheckedChange={() => toggleTransaction(item.id)} />
                                      )}
                                    </TableCell>
                                    <TableCell>{isFirst ? formatDate(item.saleDate) : <span className="text-muted-foreground text-xs">↳ split</span>}</TableCell>
                                    <TableCell className="font-mono text-sm">{isFirst ? (item.salesIdNumber || '-') : ''}</TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <span>
                                          {formatProductName(item)}
                                          {lineRateNote(item, line) && (
                                            <span className="text-muted-foreground"> ({lineRateNote(item, line)})</span>
                                          )}
                                        </span>
                                        {isFirst && <Badge variant="secondary">Split</Badge>}
                                      </div>
                                    </TableCell>
                                    <TableCell>{grade}</TableCell>
                                    <TableCell>{renderTierCell(line, index)}</TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-1">
                                        <DecimalInput
                                          value={String(line.quantity ?? '')}
                                          onChange={(v) => setSplitQty(item, index, v)}
                                          decimalPlaces={2}
                                          min={0}
                                          max={lockedKg}
                                          className="h-8 w-16"
                                        />
                                        <span className="text-xs text-muted-foreground">kg</span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <DecimalInput
                                        value={String(line.unitPrice ?? '')}
                                        onChange={(v) => setLineUnitPrice(item, index, v)}
                                        decimalPlaces={2}
                                        min={0}
                                        className="h-8 w-20"
                                      />
                                    </TableCell>
                                    <TableCell className="font-semibold text-yellow-700">£{lineTotal.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">
                                      {isFirst && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => clearLineEdit(item.id)}
                                          title="Remove split"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </React.Fragment>
                          )
                        }

                        // Single (possibly repriced) row
                        const overrideLine = edit?.lines?.[0] || null
                        const unitPriceDisplay = overrideLine
                          ? (parseFloat(overrideLine.unitPrice) || 0)
                          : (hasDiscount ? effectiveUnitPrice : basePrice)
                        return (
                          <TableRow
                            key={item.id}
                            className={`group ${!isSelected ? 'opacity-50' : ''} ${isEdited ? 'bg-yellow-50' : ''}`}
                          >
                            <TableCell>
                              <Checkbox checked={isSelected} onCheckedChange={() => toggleTransaction(item.id)} />
                            </TableCell>
                            <TableCell>{formatDate(item.saleDate)}</TableCell>
                            <TableCell className="font-mono text-sm">{item.salesIdNumber || '-'}</TableCell>
                            <TableCell>
                              {formatProductName(item)}
                              {lineRateNote(item, overrideLine) && (
                                <span className="text-muted-foreground"> ({lineRateNote(item, overrideLine)})</span>
                              )}
                              {hasDiscount && !isEdited && item.discount_note && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {item.discount_note} (-{discount}%)
                                </div>
                              )}
                            </TableCell>
                            <TableCell>{grade}</TableCell>
                            <TableCell>{renderTierCell(overrideLine, 0)}</TableCell>
                            <TableCell>{weightLabel}</TableCell>
                            <TableCell>
                              {(isEdited || hasDiscount) ? (
                                <div>
                                  <span className="line-through text-muted-foreground">£{basePrice.toFixed(2)}</span>
                                  <div className="font-medium">£{unitPriceDisplay.toFixed(2)}</div>
                                </div>
                              ) : (
                                <>£{basePrice.toFixed(2)}</>
                              )}
                            </TableCell>
                            <TableCell className={isEdited ? 'font-semibold text-yellow-700' : ''}>
                              £{lineItemTotal(item).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              {canSplit && (
                                <div className="flex items-center justify-end gap-1">
                                  {isEdited && (
                                    <Button variant="ghost" size="sm" onClick={() => clearLineEdit(item.id)} title="Reset">
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => startSplit(item)}
                                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                  >
                                    Split
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  </div>

                  {/* Branch Transactions — Mobile Cards */}
                  <div className="sm:hidden divide-y">
                    {branchData.transactions.map(item => {
                      const isSelected = selectedTransactions.has(item.id)
                      const edit = lineEdits[item.id]
                      const isEdited = !!edit
                      const isSplitItem = !!edit?.split
                      const displayPrice = lineItemTotal(item)
                      const discount = parseFloat(item.discount_percent || 0)
                      const hasDiscount = discount > 0

                      return (
                        <div
                          key={item.id}
                          className={`px-4 py-3 ${!isSelected ? 'opacity-50' : ''} ${isEdited ? 'bg-yellow-50' : ''}`}
                          onClick={() => toggleTransaction(item.id)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleTransaction(item.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm truncate flex items-center gap-1.5">
                                <span className="truncate">{formatProductName(item, { mobile: true })}</span>
                                {isSplitItem && <Badge variant="secondary" className="shrink-0">Split</Badge>}
                              </span>
                              {isSplitItem && (
                                <span className="text-xs text-muted-foreground block">{splitBreakdown(item)}</span>
                              )}
                              {hasDiscount && !isEdited && item.discount_note && (
                                <span className="text-xs text-muted-foreground">
                                  {item.discount_note} (-{discount}%)
                                </span>
                              )}
                            </div>
                            <span className={`font-mono text-sm font-semibold shrink-0 ${isEdited ? 'text-yellow-700' : ''}`}>
                              £{displayPrice.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 ml-9 text-xs text-muted-foreground">
                            <span>{formatDate(item.saleDate)}</span>
                            <span>·</span>
                            {item.block_format_id && item.block_format ? (
                              <span>
                                {(() => {
                                  const units = parseFloat(item.quantity)
                                  const totalWeight = units * (parseFloat(item.block_format.weight_kg) || 0)
                                  return `${units} Unit${units !== 1 ? 's' : ''} ${totalWeight}kg`
                                })()}
                              </span>
                            ) : item.growing_material_id ? (
                              <span>
                                {(() => {
                                  const units = parseFloat(item.quantity)
                                  const label = item.growing_material?.unit_label || 'unit'
                                  return `${units} ${label}${units !== 1 ? 's' : ''}`
                                })()}
                              </span>
                            ) : (
                              <span>{item.quantity}kg</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Payment Method and Discount Section - Side by Side on desktop, stacked on mobile */}
          <div className="border-t pt-4 flex flex-col sm:flex-row gap-4 sm:gap-6">
            {/* Payment Method Selection */}
            <div className="flex-1">
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Xero (default)">Xero (default)</SelectItem>
                  {isGoCardlessCustomer && (
                    <SelectItem value="GoCardless Direct Debit">GoCardless Direct Debit</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {isEmailRequiredForPayment(paymentMethod) && !customerEmail && (
                <p className="text-sm text-red-600 mt-2">
                  ⚠ Customer email required for {paymentMethod} payments
                </p>
              )}
            </div>

            {/* Discount and Credit Note Section */}
            <div className="space-y-4 flex-2 sm:ml-10">
            <div>
              <Label>Discount</Label>
              <RadioGroup value={discountType} onValueChange={setDiscountType}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 mt-2">
                  {/* Percentage option */}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="percentage" id="discount-percentage" />
                    <Label htmlFor="discount-percentage" className="flex items-center gap-2 font-normal">
                      Percentage
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={discountType === 'percentage' ? discountAmount : ''}
                        onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                        disabled={discountType !== 'percentage'}
                        className="w-20"
                        placeholder="0"
                      />
                      %
                    </Label>
                  </div>

                  {/* Fixed option */}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="fixed" id="discount-fixed" />
                    <Label htmlFor="discount-fixed" className="flex items-center gap-2 font-normal">
                      Fixed £
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={discountType === 'fixed' ? discountAmount : ''}
                        onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                        disabled={discountType !== 'fixed'}
                        className="w-20"
                        placeholder="0.00"
                      />
                    </Label>
                  </div>
                </div>
              </RadioGroup>
              {discountAmount > 0 && (
                <div className="mt-2">
                  <Input
                    type="text"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="Reason for discount (shown on invoice)"
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {isCourierCustomer && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="whitespace-nowrap">Shipping Charge</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" onClick={addShippingCharge}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {shippingCharges.map((charge, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <DecimalInput
                      value={charge}
                      onChange={(val) => updateShippingCharge(index, val)}
                      placeholder="0.00"
                      className="w-32"
                    />
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeShippingCharge(index)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                {shippingCharges.length > 0 && (
                  <span className="text-xs text-muted-foreground">425 · Postage, Freight & Courier · 20% VAT</span>
                )}
              </div>
            )}
            </div>
          </div>

          {/* Totals Display */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>£{totals.subtotal}</span>
            </div>
            {parseFloat(totals.discount) > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Discount ({discountType === 'percentage' ? `${discountAmount}%` : 'Fixed'}):</span>
                <span>-£{totals.discount}</span>
              </div>
            )}
            {parseFloat(totals.shippingChargesTotal) > 0 && (
              <div className="flex justify-between text-green-700">
                <span>{shippingCharges.filter(v => v && v > 0).length > 1 ? `Shipping (×${shippingCharges.filter(v => v && v > 0).length}):` : 'Shipping Charge:'}</span>
                <span>+£{totals.shippingChargesTotal}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>£{totals.total}</span>
            </div>
          </div>
        </div>
        ) : (
          // Draft Preview Section
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-green-900">Draft Created Successfully</h3>
                  <p className="text-sm text-green-700 mt-1">Invoice #{draftInvoice?.xero_invoice_number}</p>
                </div>
                <Badge className="bg-green-100 text-green-800">DRAFT</Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Invoice Number</p>
                  <p className="font-mono font-semibold">{draftInvoice?.xero_invoice_number}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Amount</p>
                  <p className="font-semibold">£{draftInvoice?.total_amount?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-gray-50">
              <p className="text-sm text-gray-600 mb-3">Invoice PDF:</p>
              {draftInvoice?.xero_pdf_url ? (
                <div className="bg-white border border-gray-200 rounded p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1v-6z" />
                    </svg>
                    <div>
                      <p className="font-medium text-gray-900">Draft Invoice #{draftInvoice?.xero_invoice_number}</p>
                      <p className="text-xs text-gray-500">Generated by Xero</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const link = document.createElement('a')
                        link.href = draftInvoice.xero_pdf_url
                        link.download = `INV-${draftInvoice?.xero_invoice_number}.pdf`
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)
                      }}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    >
                      Download
                    </button>
                    <a
                      href={draftInvoice.xero_pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-sm bg-green-50 hover:bg-green-100 text-green-700 rounded transition-colors"
                    >
                      View
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">
                    PDF will be available in 15-30 seconds once Xero processes the draft.
                  </p>
                  <p className="text-xs text-gray-500">
                    Click the "Check for PDF" button above to refresh when ready.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Include Account Summary */}
        {showDraftPreview && (
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include-statement"
                  checked={includeStatement}
                  onCheckedChange={setIncludeStatement}
                />
                <Label htmlFor="include-statement" className="text-sm font-medium cursor-pointer">
                  Include account summary
                </Label>
              </div>
              {outstandingCount > 0 && (
                <span className="text-xs text-muted-foreground">
                  Outstanding invoices: ({outstandingCount})
                </span>
              )}
            </div>

            {includeStatement && (
              <RadioGroup
                value={statementType}
                onValueChange={setStatementType}
                className="flex gap-4 pl-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="since_zero" id="since-zero" />
                  <Label htmlFor="since-zero" className="text-sm">Since zero balance</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="transactions" id="last-transactions" />
                  <Label htmlFor="last-transactions" className="text-sm">Last 10 transactions</Label>
                </div>
              </RadioGroup>
            )}
          </div>
        )}

        {/* Credit note banner */}
        {creditNoteInfo && creditNoteInfo.totalCredit > 0 && !showDraftPreview && (
          <div className={`rounded-lg border p-3 text-sm ${
            creditNoteInfo.totalCredit >= parseFloat(totals.total)
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            {creditNoteInfo.totalCredit >= parseFloat(totals.total)
              ? `This invoice will be fully covered by existing credit (Amount due: £0.00)`
              : `This customer has £${creditNoteInfo.totalCredit.toFixed(2)} credit available — it will be automatically applied`
            }
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          {!isCustomerMapped ? (
            <Button
              onClick={() => setShowMappingModal(true)}
              disabled={checkingMapping}
            >
              {checkingMapping ? 'Checking...' : 'Map Customer'}
            </Button>
          ) : showDraftPreview ? (
            // Buttons shown after draft is created
            <>
              <Button variant="outline" onClick={clearDraftPreview} disabled={isCreating}>
                Back to Edit
              </Button>
              <Button variant="outline" disabled>
                Preview Draft
              </Button>
              <Button
                onClick={async () => {
                  setIsCreating(true)
                  // Show progress modal for authorise & send
                  const steps = [
                    { label: 'Authorising & sending invoice...', status: 'active', detail: null },
                    { label: 'Checking for credit notes...', status: 'pending', detail: null },
                    { label: 'Done', status: 'pending', detail: null }
                  ]
                  setProgressSteps([...steps])
                  setProgressPercent(15)
                  setShowProgressModal(true)

                  const updateStep = (index, status, detail = null) => {
                    steps[index] = { ...steps[index], status, detail }
                    setProgressSteps([...steps])
                  }

                  try {
                    const { data: sendData, error: sendError } = await supabase.functions.invoke('xero-invoices', {
                      body: {
                        action: 'authorise-and-send',
                        invoice_id: draftInvoice.invoice_id,
                        email_to: customerEmail,
                        ...(includeStatement && {
                          include_statement: true,
                          statement_type: statementType
                        })
                      }
                    })

                    if (sendError || !sendData?.success) {
                      throw new Error(sendData?.error || 'Failed to send invoice')
                    }

                    updateStep(0, 'done', 'Invoice sent')
                    setProgressPercent(50)

                    // Credit notes step
                    updateStep(1, 'active')
                    if (sendData.credit_applied) {
                      updateStep(1, 'done', `£${sendData.credit_total_applied?.toFixed(2)} credit applied`)
                    } else {
                      updateStep(1, 'done', 'No credit notes')
                    }
                    setProgressPercent(80)

                    // Done
                    updateStep(2, 'done')
                    setProgressPercent(100)

                    // Wait for data refresh
                    await onDataRefresh()
                    clearDraftPreview()

                    // Check if email failed (partial success)
                    if (sendData.email_sent === false) {
                      setShowProgressModal(false)
                      onClose()
                      onEmailWarning?.(customerId, sendData.email_error || 'Unknown email delivery error')
                      return
                    }

                    // Build success message
                    let successMsg = 'Invoice authorised and sent successfully'
                    const paymentMethodValue = paymentMethod === 'Xero (default)' ? 'xero'
                      : paymentMethod === 'GoCardless Direct Debit' ? 'gocardless'
                      : paymentMethod === 'Cash' ? 'cash'
                      : 'xero'

                    if (paymentMethodValue === 'gocardless' && sendData.payment_automation_triggered) {
                      successMsg = '✓ Invoice authorised and sent\n✓ GoCardless payment automation triggered'
                    } else if (paymentMethodValue === 'xero' && sendData.online_payment_url) {
                      successMsg = `✓ Invoice authorised and sent\n✓ Online payment URL: ${sendData.online_payment_url}\n\n(URL copied to clipboard)`
                      navigator.clipboard.writeText(sendData.online_payment_url).catch(() => {})
                    } else if (paymentMethodValue === 'cash') {
                      successMsg = sendData.cash_payment_recorded
                        ? '✓ Invoice authorised and sent\n✓ Cash payment recorded — £0.00 due'
                        : '✓ Invoice authorised and sent\n⚠ Cash payment failed — check Xero'
                    }

                    // Append credit info
                    if (sendData.credit_applied && sendData.credit_total_applied > 0) {
                      successMsg += `\n✓ Credit applied: £${sendData.credit_total_applied.toFixed(2)}`
                      successMsg += `\n  Amount due: £${sendData.credit_remaining_due?.toFixed(2) || '0.00'}`
                      if (sendData.credit_remaining_on_account > 0) {
                        successMsg += `\n  Remaining credit on account: £${sendData.credit_remaining_on_account.toFixed(2)}`
                      }
                    }

                    await new Promise(r => setTimeout(r, 1000))
                    setShowProgressModal(false)
                    onClose()
                    setSuccessDialogMessage(successMsg)
                    setShowSuccessDialog(true)
                  } catch (error) {
                    setShowProgressModal(false)
                    showError({
                      message: error.message || 'Invoice sent but failed to refresh data. Please reload the page or try again.',
                      data: error.data || { error: 'REFRESH_FAILED' }
                    })
                  } finally {
                    setIsCreating(false)
                  }
                }}
                disabled={isCreating}
              >
                {isCreating ? (isReconciling ? 'Reconciling...' : 'Sending...') : 'Authorise & Send'}
              </Button>
            </>
          ) : (
            // Buttons shown in form view
            <>
              {/* Show mapping button if customer not mapped */}
              {!isCustomerMapped && !checkingMapping && (
                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-muted-foreground">
                    Customer must be mapped to Xero before creating invoices.
                  </p>
                  <Button
                    onClick={() => setShowMappingModal(true)}
                    variant="outline"
                    className="w-full"
                  >
                    Map Customer to Xero
                  </Button>
                </div>
              )}

              {/* Show loading state during mapping check */}
              {checkingMapping && (
                <Button disabled className="w-full">
                  Checking mapping status...
                </Button>
              )}

              {isCustomerMapped && !checkingMapping && (
                <>
                  <Button
                    onClick={() => setShowCashConfirm(true)}
                    disabled={isCreating || isMarkingPaid || selectedTransactions.size === 0}
                  >
                    {isMarkingPaid ? 'Marking...' : 'Paid with Cash'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowSendConfirm(true)}
                    disabled={isCreating || selectedTransactions.size === 0 || (isEmailRequiredForPayment(paymentMethod) && !customerEmail)}
                  >
                    {isCreating ? 'Creating...' : 'Create Invoice'}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogFooter>

        {/* Create Invoice confirmation dialog */}
        <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Create Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Choose how to process the invoice for {customerName}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowSendConfirm(false)}>Cancel</AlertDialogCancel>
              <Button variant="outline" onClick={() => { setShowSendConfirm(false); handleCreateDraft(); }}>
                Create Draft
              </Button>
              <AlertDialogAction onClick={() => { setShowSendConfirm(false); handleCreateAndSend(); }}>
                Create & Send
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Paid with Cash confirmation dialog */}
        <AlertDialog open={showCashConfirm} onOpenChange={setShowCashConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Paid with Cash?</AlertDialogTitle>
              <AlertDialogDescription>
                This will clear {selectedTransactions.size} item{selectedTransactions.size !== 1 ? 's' : ''} from accruing invoices without creating a Xero invoice. This action cannot be easily undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowCashConfirm(false)} disabled={isMarkingPaid}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleMarkAsPaid} disabled={isMarkingPaid}>
                {isMarkingPaid ? 'Marking...' : 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </DialogContent>
    </Dialog>

    {/* Disregard Confirmation Dialog - Only rendered when main Dialog is closed */}
    {!isOpen && (
      <AlertDialog open={showDisregardDialog} onOpenChange={setShowDisregardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disregard Sale from Invoicing?</AlertDialogTitle>
            <AlertDialogDescription>
              This sale will be excluded from invoice generation but will remain in the system for reference.
              You can still view it in the customer's uninvoiced transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDisregardCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisregard}>Disregard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}

    {/* Error Dialog */}
    <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{errorDialogTitle}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {errorDialogMessage}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setShowErrorDialog(false)}>
            Close
          </AlertDialogCancel>
          {/* Show mapping action for CUSTOMER_NOT_MAPPED errors */}
          {errorDialogFullError?.data?.error === 'CUSTOMER_NOT_MAPPED' && (
            <AlertDialogAction
              onClick={() => {
                setShowErrorDialog(false)
                setShowMappingModal(true)
              }}
            >
              Map Customer Now
            </AlertDialogAction>
          )}
          {/* Show retry for other retryable errors */}
          {errorDialogRetryable && errorDialogFullError?.data?.error !== 'CUSTOMER_NOT_MAPPED' && (
            <AlertDialogAction onClick={() => setShowErrorDialog(false)}>
              Try Again
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Progress Modal */}
    <Dialog open={showProgressModal} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Creating Invoice</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Progress value={progressPercent} className="w-full" />
          <div className="space-y-2">
            {progressSteps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {step.status === 'active' && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                {step.status === 'done' && <Check className="h-4 w-4 text-green-500" />}
                {step.status === 'error' && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                {step.status === 'pending' && <Circle className="h-4 w-4 text-muted-foreground/30" />}
                <span className={step.status === 'pending' ? 'text-muted-foreground/50' : ''}>
                  {step.detail || step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Success Dialog */}
    <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Success</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {successDialogMessage}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => setShowSuccessDialog(false)}>
            Close
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Task 5.1: Customer Mapping Modal */}
    <CustomerMappingModal
      open={showMappingModal}
      onClose={() => setShowMappingModal(false)}
      customerID={customerId}
      customerName={customerName}
      customerEmail={customerEmail}
      customerPhone={customerPhone}
      onMappingComplete={handleMappingComplete}
    />
    </>
  )
}

export default function InvoiceDashboard({ user, onLogout }) {
  const navigate = useNavigate()

  // Uninvoiced customers state
  const [customersWithUninvoiced, setCustomersWithUninvoiced] = useState([])
  const [lastInvoiceDates, setLastInvoiceDates] = useState({})
  const [uninvoicedSortConfig, setUninvoicedSortConfig] = useState({ key: 'name', direction: 'asc' })

  // Memoized flattened table data for performance
  const flattenedUninvoicedRows = useMemo(() => {
    return customersWithUninvoiced.flatMap(customerGroup =>
      customerGroup.branches.map(branch => ({
        customer: customerGroup.customer,
        branch
      }))
    )
  }, [customersWithUninvoiced])

  // All Invoices state
  const [allInvoices, setAllInvoices] = useState([])
  const [filteredInvoices, setFilteredInvoices] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [invoicesSortConfig, setInvoicesSortConfig] = useState({ key: 'invoice_no', direction: 'desc' })

  // Draft Invoices state (Task 13)
  const [draftInvoices, setDraftInvoices] = useState([])
  const [showDraftPreviewModal, setShowDraftPreviewModal] = useState(false)
  const [selectedDraft, setSelectedDraft] = useState(null)

  // Draft Invoice Management state (Task 14)
  const [selectedDraftIds, setSelectedDraftIds] = useState(new Set())
  const [showRevertConfirm, setShowRevertConfirm] = useState(false)
  const [draftToRevert, setDraftToRevert] = useState(null)
  const [showBatchAuthoriseConfirm, setShowBatchAuthoriseConfirm] = useState(false)
  const [showBatchResults, setShowBatchResults] = useState(false)
  const [batchResults, setBatchResults] = useState(null)
  const [isReverting, setIsReverting] = useState(false)
  const [isAuthorising, setIsAuthorising] = useState(false)

  // GoCardless next payout (tooltip)
  const [nextPayout, setNextPayout] = useState(null)
  const [nextPayoutLoading, setNextPayoutLoading] = useState(true)

  // PDF Preview state (Blob URL approach)
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState(null)
  const [fetchingPdfId, setFetchingPdfId] = useState(null)

  // Xero configuration
  const [xeroTenantId, setXeroTenantId] = useState(null) // NEW: For constructing Xero links
  
  // Modals
  // showPreview, previewData, singlePDFMode removed - Xero-first (Task 5 v2)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewData, setReviewData] = useState(null)
  const [reviewCustomerName, setReviewCustomerName] = useState('')
  const [reviewCustomerEmail, setReviewCustomerEmail] = useState('')
  const [reviewCustomerBranches, setReviewCustomerBranches] = useState([])
  const [reviewSelectedBranchIds, setReviewSelectedBranchIds] = useState([])
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [selectedInvoiceForEmail, setSelectedInvoiceForEmail] = useState(null)

  // Success dialog state (Task 5.1: Replace native alert with proper modal)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Email warning state (email failed but invoice authorised successfully)
  const [showEmailWarning, setShowEmailWarning] = useState(false)
  const [emailWarningMessage, setEmailWarningMessage] = useState('')
  const [emailWarningCustomerId, setEmailWarningCustomerId] = useState(null)

  // Task 12: Mark as Paid state (US-REC-001: moved to MarkAsPaidDialog component)
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false)
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState(null)

  // US-REC-001: Invoice Detail Panel state
  const [showDetailPanel, setShowDetailPanel] = useState(false)
  const [selectedDetailInvoice, setSelectedDetailInvoice] = useState(null)

  // US-REC-001: Credit Note Modal state
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false)
  const [selectedCreditNoteInvoice, setSelectedCreditNoteInvoice] = useState(null)

  // Write-off dialog state
  const [showWriteOffDialog, setShowWriteOffDialog] = useState(false)
  const [writeOffInvoice, setWriteOffInvoice] = useState(null)
  const [writeOffReason, setWriteOffReason] = useState('')
  const [isWritingOff, setIsWritingOff] = useState(false)

  // General state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [loadingCustomers, setLoadingCustomers] = useState({})

  // Xero sync state
  const [syncingInvoices, setSyncingInvoices] = useState({}) // Track invoices currently syncing
  // eslint-disable-next-line no-unused-vars
  const [syncStatuses, setSyncStatuses] = useState({}) // Cache sync status for invoices (kept for future use)
  const [copiedPaymentUrl, setCopiedPaymentUrl] = useState(null) // Track copied payment URL

  // Customer detail modal state (for quick edits from accruing table)
  const [selectedCustomerForDetail, setSelectedCustomerForDetail] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)

  // Xero sync diff dialog state (pre-authorise verification)
  const [showSyncDiffDialog, setShowSyncDiffDialog] = useState(false)
  const [syncDiffData, setSyncDiffData] = useState(null)
  const [syncDiffSyncing, setSyncDiffSyncing] = useState(false)
  const [pendingAuthoriseAction, setPendingAuthoriseAction] = useState(null)

  // Warning logic for accruing invoices table
  const getCustomerWarnings = useCallback((row) => {
    const warnings = []
    if (!row.customer.xero_contact_id) {
      warnings.push('No Xero contact linked to this customer')
    }
    if (!row.customer.email || row.customer.email.endsWith('@placeholder.com')) {
      warnings.push('Missing customer email address')
    }
    if (!row.branch.address) {
      warnings.push('Missing branch delivery address')
    }
    return warnings
  }, [])

  // Open CustomerDetailModal from accruing table
  const handleCustomerClick = useCallback(async (customer) => {
    try {
      const fullCustomer = await fetchCustomer(customer.id)
      // Transform to match CustomerDetailModal expected format
      const companyBranch = fullCustomer.branches?.find(b => b.branch_type === 'company') ||
        fullCustomer.branches?.find(b => b.name && (b.name.toLowerCase().includes('main') || b.name.toLowerCase().includes('head') || b.name.toLowerCase().includes('office'))) ||
        fullCustomer.branches?.[0]

      const transformedCustomer = {
        id: fullCustomer.id,
        companyName: fullCustomer.name,
        email: fullCustomer.email,
        paymentTermsDays: fullCustomer.payment_terms ? parseInt(fullCustomer.payment_terms) : 30,
        payment_method: fullCustomer.payment_method || null,
        phone: fullCustomer.phone || '',
        reference_code: fullCustomer.reference_code || null,
        addressLine1: companyBranch?.address_line_1 || '',
        addressLine2: companyBranch?.address_line_2 || '',
        addressLine3: companyBranch?.address_line_3 || '',
        city: companyBranch?.city || '',
        postcode: companyBranch?.postcode || '',
        country: companyBranch?.country || 'United Kingdom',
        branches: fullCustomer.branches?.filter(b => b.branch_type !== 'company').map(branch => ({
          id: branch.id,
          branchName: branch.name,
          branchAddress: branch.address,
          branchPhone: branch.phone || '',
          branchEmail: branch.email || '',
          branchType: branch.branch_type || 'branch'
        })) || []
      }
      setSelectedCustomerForDetail(transformedCustomer)
      setDetailModalOpen(true)
    } catch (err) {
      console.error('Error loading customer details:', err)
      toast.error('Failed to load customer details')
    }
  }, [])

  const loadUninvoicedData = useCallback(async () => {
    try {
      const [customersData, lastDatesData] = await Promise.all([
        fetchCustomersWithUninvoicedSales(),
        fetchLastInvoiceDateByCustomer()
      ])

      // Filter out £0 totals but keep negative balances (customer credits)
      const filteredCustomersData = customersData.filter(customerGroup => {
        const totalAmount = customerGroup.branches.reduce((sum, branch) =>
          sum + parseFloat(branch.total), 0
        )
        // Keep if total is negative (credit) or greater than 0
        return totalAmount !== 0
      })

      setCustomersWithUninvoiced(filteredCustomersData)
      setLastInvoiceDates(lastDatesData)

    } catch (err) {
      console.error('Error loading accruing sales data:', err)
      setError('Unable to load accruing sales data. Please check your connection and try again.')
    }
  }, [])

  const loadAllInvoices = useCallback(async () => {
    try {
      // US-REC-001: Fetch invoices with balance data for status display
      const invoicesData = await fetchInvoicesWithBalances()
      setAllInvoices(invoicesData)
      setFilteredInvoices(invoicesData)
    } catch (err) {
      console.error('Error loading invoices:', err)
      setError('Unable to load invoice history. Please check your connection and try again.')
    }
  }, [])

  // Load draft invoices (Task 13)
  const loadDraftInvoices = useCallback(async () => {
    try {
      const draftsData = await fetchDraftInvoices()
      setDraftInvoices(draftsData)
    } catch (err) {
      console.error('Error loading draft invoices:', err)
      setError('Unable to load draft invoices. Please check your connection and try again.')
    }
  }, [])

  /**
   * Handle payment sync completion
   * Refreshes invoice list to reflect updated payment statuses
   *
   * @param {Object} result - Sync result from useXeroPaymentSync hook
   */
  const handlePaymentSyncComplete = useCallback(async (result) => {
    console.log('[InvoiceDashboard] Payment sync complete:', result)

    // Refresh invoice list to show updated payment statuses
    await loadAllInvoices()
  }, [loadAllInvoices])

  // Load Xero configuration on mount
  useEffect(() => {
    const loadXeroConfig = async () => {
      try {
        const config = await fetchXeroConfig()
        setXeroTenantId(config.tenant_id)
      } catch (err) {
        console.error('Error loading Xero config:', err)
        // Non-critical error, Xero links just won't work
      }
    }
    loadXeroConfig()
  }, [])

  // Fetch next GoCardless payout for tooltip
  useEffect(() => {
    fetchNextGoCardlessPayout()
      .then(data => setNextPayout(data))
      .catch(err => console.warn('[InvoiceDashboard] Next payout fetch failed:', err))
      .finally(() => setNextPayoutLoading(false))
  }, [])

  const loadInitialData = useCallback(async () => {
    await Promise.all([
      loadUninvoicedData(),
      loadAllInvoices(),
      loadDraftInvoices()
    ])
  }, [loadUninvoicedData, loadAllInvoices, loadDraftInvoices])

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await loadInitialData()
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [loadInitialData])

  // Real-time subscription for invoice paid status updates
  useEffect(() => {
    // Subscribe to invoice paid status changes
    const subscription = supabase
      .channel('invoice-paid-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'invoices'
      }, (payload) => {
        console.log('[InvoiceDashboard] Invoice updated:', payload)

        // Update invoice in all relevant state arrays for any change
        const updateInvoiceInState = (prev) => prev.map(invoice =>
          invoice.id === payload.new.id ? { ...invoice, ...payload.new } : invoice
        )

        setFilteredInvoices(updateInvoiceInState)
        setAllInvoices(updateInvoiceInState)

        // Notify: paid status changed
        if (payload.old?.paid !== payload.new?.paid && payload.new.paid) {
          toast.success('Invoice marked as paid', {
            description: `${payload.new.invoice_no} - Payment received`
          })
        }

        // Notify: invoice voided
        if (payload.old?.xero_invoice_status !== 'voided' && payload.new?.xero_invoice_status === 'voided') {
          toast.error('Invoice voided', {
            description: `${payload.new.invoice_no} was voided in Xero`
          })
        }

        // Notify: invoice number changed
        if (payload.old?.invoice_no && payload.new?.invoice_no && payload.old.invoice_no !== payload.new.invoice_no) {
          toast.info('Invoice number updated', {
            description: `${payload.old.invoice_no} → ${payload.new.invoice_no}`
          })
        }
      })
      .subscribe()

    // Cleanup subscription on unmount
    return () => {
      console.log('[InvoiceDashboard] Unsubscribing from invoice paid updates')
      subscription.unsubscribe()
    }
  }, [])

  // Real-time subscription for draft invoice status updates
  useEffect(() => {
    // Subscribe to draft invoice updates (xero_sync_status changes)
    const subscription = supabase
      .channel('draft-invoice-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'invoices',
        filter: 'xero_invoice_id=not.is.null'  // Only Xero-synced invoices
      }, (payload) => {
        console.log('[InvoiceDashboard] Draft invoice updated:', payload)

        // Update draft invoices list
        setDraftInvoices(prev => prev.map(invoice =>
          invoice.id === payload.new.id ? { ...invoice, ...payload.new } : invoice
        ))

        // Show notification if sync status changed to failed
        if (payload.new.xero_sync_status === 'failed' &&
            payload.old?.xero_sync_status !== 'failed') {
          toast.error('Invoice sync failed', {
            description: `${payload.new.invoice_no} - ${payload.new.xero_sync_error || 'Unknown error'}`
          })
        }
      })
      .subscribe()

    // Cleanup subscription on unmount
    return () => {
      console.log('[InvoiceDashboard] Unsubscribing from draft invoice updates')
      subscription.unsubscribe()
    }
  }, [])

  // Real-time subscription for draft invoice PDF generation
  useEffect(() => {
    // Subscribe to PDF URL updates for draft invoices
    const subscription = supabase
      .channel('draft-pdf-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'invoices',
        filter: 'is_draft=eq.true'  // Only draft invoices
      }, (payload) => {
        // Only process if xero_pdf_url changed
        if (payload.new.xero_pdf_url !== payload.old?.xero_pdf_url) {
          console.log('[InvoiceDashboard] PDF generated for draft:', payload.new.invoice_no)

          // Update draft invoices list with new PDF URL
          setDraftInvoices(prev => prev.map(invoice =>
            invoice.id === payload.new.id
              ? { ...invoice, xero_pdf_url: payload.new.xero_pdf_url }
              : invoice
          ))

          // Show success notification if PDF is now available
          if (payload.new.xero_pdf_url && !payload.old?.xero_pdf_url) {
            toast.success('PDF Ready', {
              description: `Invoice ${payload.new.invoice_no} PDF is now available`
            })
          }
        }
      })
      .subscribe()

    // Cleanup subscription on unmount
    return () => {
      console.log('[InvoiceDashboard] Unsubscribing from draft PDF updates')
      subscription.unsubscribe()
    }
  }, [])

  // Real-time subscription for GoCardless payment status changes
  useEffect(() => {
    const subscription = supabase
      .channel('invoice-gc-payment-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'payments',
        filter: 'source=eq.gocardless'
      }, (payload) => {
        const invoiceId = payload.new?.invoice_id
        if (!invoiceId) return

        console.log('[InvoiceDashboard] GoCardless payment updated:', payload.new.gocardless_payment_status)

        const newStatus = payload.new.gocardless_payment_status
        const oldStatus = payload.old?.gocardless_payment_status

        // Update matching invoice's GC status in local state
        const updateGcStatus = (prev) => prev.map(invoice =>
          invoice.id === invoiceId
            ? {
                ...invoice,
                gc_payment_status: newStatus,
                gc_charge_date: payload.new.gocardless_charge_date,
                gc_failure_reason: payload.new.gocardless_failure_reason
              }
            : invoice
        )

        setFilteredInvoices(updateGcStatus)
        setAllInvoices(updateGcStatus)

        // Toast for meaningful transitions
        if (oldStatus !== newStatus) {
          switch (newStatus) {
            case 'confirmed':
              toast.success('Payment confirmed', {
                description: 'GoCardless payment confirmed by bank'
              })
              break
            case 'paid_out':
              toast.success('Payment completed', {
                description: 'GoCardless funds have been transferred'
              })
              break
            case 'failed':
              toast.error('Payment failed', {
                description: payload.new.gocardless_failure_reason || 'Direct Debit payment could not be processed'
              })
              break
            case 'charged_back':
              toast.error('Payment charged back', {
                description: 'Customer disputed the Direct Debit payment'
              })
              break
            case 'late_failure_settled':
              toast.error('Late failure', {
                description: payload.new.gocardless_failure_reason || 'Payment failed after initial confirmation'
              })
              break
          }
        }
      })
      .subscribe()

    return () => {
      console.log('[InvoiceDashboard] Unsubscribing from GC payment updates')
      subscription.unsubscribe()
    }
  }, [])

  // PDF Blob URL preview effect
  useEffect(() => {
    // Reset state when modal closes or draft changes
    if (!selectedDraft?.xero_pdf_url) {
      setPdfBlobUrl(null)
      setPdfLoading(false)
      setPdfError(null)
      return
    }

    // Fetch PDF and create blob URL
    setPdfLoading(true)
    setPdfError(null)

    fetch(selectedDraft.xero_pdf_url)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to load PDF: ${res.status} ${res.statusText}`)
        }
        return res.blob()
      })
      .then(blob => {
        // Create blob URL (bypasses X-Frame-Options!)
        const url = URL.createObjectURL(blob)
        setPdfBlobUrl(url)
        setPdfLoading(false)
      })
      .catch(error => {
        console.error('PDF load error:', error)
        setPdfError(error.message)
        setPdfLoading(false)
      })

    // Cleanup blob URL on unmount or when draft changes
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [selectedDraft?.xero_pdf_url])

  const refreshData = async () => {
    setLoading(true)
    setError(null)
    try {
      await loadInitialData()
    } finally {
      setLoading(false)
    }
  }

  // Handle search in all invoices (excluding Xero drafts, but including historical imports)
  useEffect(() => {
    // Filter logic:
    // - Include: is_draft = false (finalized invoices)
    // - Include: is_draft = true AND xero_invoice_id = null (historical imported invoices)
    // - Exclude: is_draft = true AND xero_invoice_id exists (Xero drafts - they have their own section)
    const nonDraftInvoices = allInvoices.filter(inv =>
      !inv.is_draft || (inv.is_draft && !inv.xero_invoice_id)
    )

    // Apply active filter (US-REC-001: Use calculated_status for status-based filters)
    let filtered
    switch (activeFilter) {
      case 'gocardless':
        // Include any invoice routed through GoCardless — either paid via GC
        // (payment_source) or set up to pay via GC (payment_method, awaiting collection)
        filtered = nonDraftInvoices.filter(inv =>
          inv.payment_source === 'gocardless' || inv.payment_method === 'gocardless'
        )
        break
      case 'xero':
        // Any invoice that exists in the Xero account
        filtered = nonDraftInvoices.filter(inv => !!inv.xero_invoice_id)
        break
      case 'paid':
        // Use calculated_status which respects partial payments and credits
        filtered = nonDraftInvoices.filter(inv => inv.calculated_status === 'paid')
        break
      case 'unpaid':
        // All invoices that need any payment action
        filtered = nonDraftInvoices.filter(inv =>
          inv.calculated_status === 'unpaid' || inv.calculated_status === 'partially_paid'
        )
        break
      case 'partially_paid':
        filtered = nonDraftInvoices.filter(inv => inv.calculated_status === 'partially_paid')
        break
      case 'overdue':
        filtered = nonDraftInvoices.filter(inv => {
          // Must be unpaid or partially paid
          if (inv.calculated_status === 'paid' || inv.calculated_status === 'voided') return false
          if (!inv.customer?.payment_terms) return false
          const dueDate = addDays(parseISO(inv.date), parseInt(inv.customer.payment_terms, 10))
          return isAfter(new Date(), dueDate)
        })
        break
      case 'written_off':
        // Paid via credit notes (write-offs)
        filtered = nonDraftInvoices.filter(inv =>
          inv.calculated_status === 'paid' && parseFloat(inv.amount_credited) > 0
        )
        break
      case 'voided':
        filtered = nonDraftInvoices.filter(inv => inv.calculated_status === 'voided')
        break
      default:
        filtered = [...nonDraftInvoices]
    }

    // Apply search term filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(invoice =>
        invoice.invoice_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.branch?.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply current sorting to filtered results
    if (invoicesSortConfig.key) {
      filtered.sort((a, b) => {
        let aVal, bVal

        switch (invoicesSortConfig.key) {
          case 'date':
            aVal = new Date(a.date)
            bVal = new Date(b.date)
            break
          case 'invoice_no':
            aVal = a.invoice_no.toLowerCase()
            bVal = b.invoice_no.toLowerCase()
            break
          case 'customer':
            aVal = (a.customer?.name || '').toLowerCase()
            bVal = (b.customer?.name || '').toLowerCase()
            break
          case 'branch':
            aVal = (a.branch?.name || 'main').toLowerCase()
            bVal = (b.branch?.name || 'main').toLowerCase()
            break
          case 'total':
            aVal = parseFloat(a.total) || 0
            bVal = parseFloat(b.total) || 0
            break
          case 'paid':
            aVal = a.paid ? 1 : 0
            bVal = b.paid ? 1 : 0
            break
          default:
            return 0
        }

        if (aVal < bVal) {
          return invoicesSortConfig.direction === 'asc' ? -1 : 1
        }
        if (aVal > bVal) {
          return invoicesSortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    setFilteredInvoices(filtered)
  }, [searchTerm, allInvoices, invoicesSortConfig, activeFilter])

  // Process invoices for display, preserving user's sort order
  // Adds metadata to each invoice to control when to show date/customer labels
  const consolidatedInvoices = useMemo(() => {
    const result = []
    let previousCustomerId = null
    let previousDate = null

    filteredInvoices.forEach((invoice) => {
      // Use customer_id from invoice record (not invoice.customer.id which isn't fetched)
      const currentCustomerId = invoice.customer_id || 'unknown'
      const currentDate = invoice.date
      const dateKey = format(parseISO(currentDate), 'yyyy-MM-dd')

      // Show customer name when customer changes from previous row
      const showCustomer = currentCustomerId !== previousCustomerId

      // Show date when date changes from previous row
      const showDate = currentDate !== previousDate

      result.push({
        invoice,
        showCustomer,
        showDate,
        dateKey,
        customer: invoice.customer,
        date: invoice.date
      })

      previousCustomerId = currentCustomerId
      previousDate = currentDate
    })

    return result
  }, [filteredInvoices])


  const handleReviewTransactions = async (customerId, branchId) => {
    const loadingKey = `${customerId}-${branchId}`
    try {
      setLoadingCustomers(prev => ({ ...prev, [loadingKey]: true }))

      // Get customer data
      const customerGroup = customersWithUninvoiced.find(group => group.customer.id === customerId)
      if (!customerGroup) {
        throw new Error('Customer data not found')
      }

      // Get the specific branch
      const branch = customerGroup.branches.find(b => b.id === branchId)
      if (!branch) {
        throw new Error('Branch data not found')
      }

      // Get detailed sales data for this specific branch only
      const branchSales = await fetchUninvoicedSalesByCustomerBranch(customerId, branchId)

      // Set review data and open modal
      setReviewData(branchSales)
      setReviewCustomerName(customerGroup.customer.name)
      setReviewCustomerEmail(customerGroup.customer.email || '')
      setReviewCustomerBranches([branch]) // Only show the selected branch
      setReviewSelectedBranchIds([branchId]) // Pre-select only this branch
      setShowReviewModal(true)

    } catch (err) {
      console.error('Error loading review data:', err)
      setError('Unable to load transaction data for review. Please try again.')
    } finally {
      setLoadingCustomers(prev => ({ ...prev, [loadingKey]: false }))
    }
  }

  const handleGenerateInvoiceFromReview = () => {
    // Close review modal
    setShowReviewModal(false)

    // Find the customer ID from the review data
    if (reviewData && reviewData.length > 0) {
      const customerId = reviewData[0].customer_id
      handleGenerateInvoice(customerId)
    }
  }

  const handleDisregardSale = async (saleId) => {
    try {
      await disregardSale(saleId)
      // Update the review data to remove the disregarded sale
      setReviewData(prev => prev.filter(sale => sale.id !== saleId))
      // Refresh the main data to reflect the change
      await refreshData()
    } catch (error) {
      console.error('Error disregarding sale:', error)
      setError('Failed to disregard sale. Please try again.')
    }
  }

  /**
   * Xero-First Invoice Creation (Task 5 v2)
   * Sends transactions directly to Xero, bypassing preview modal and PDF generation
   */
  const handleGenerateInvoice = async (customerId) => {
    // Use reviewSelectedBranchIds from the review modal state
    if (reviewSelectedBranchIds.length === 0) {
      setError('Please select at least one branch to send to Xero.')
      return
    }

    try {
      setLoadingCustomers(prev => ({ ...prev, [customerId]: true }))

      // Get customer data
      const customerGroup = customersWithUninvoiced.find(group => group.customer.id === customerId)
      if (!customerGroup) {
        throw new Error('Customer data not found')
      }

      // Get detailed sales data for selected branches
      const selectedTransactions = []
      for (const branchId of reviewSelectedBranchIds) {
        const branchSales = await fetchUninvoicedSalesByCustomerBranch(customerId, branchId)

        // Format transactions for sendInvoicesToXero
        branchSales.forEach(sale => {
          selectedTransactions.push({
            sale_id: sale.id,
            customer_id: customerId,
            branch_id: branchId === 'main' ? null : branchId,
            date: sale.date,
            total: sale.sale_items.reduce((sum, item) => sum + parseFloat(item.sub_total), 0)
          })
        })
      }

      if (selectedTransactions.length === 0) {
        throw new Error('No transactions found for selected branches')
      }

      // Send to Xero (creates invoices directly in Xero)
      console.log('[Invoice Dashboard] Sending transactions to Xero:', {
        transactionCount: selectedTransactions.length,
        branchCount: reviewSelectedBranchIds.length
      })

      const results = await sendInvoicesToXero(selectedTransactions)

      // Check results
      const successCount = results.filter(r => r.success).length
      const failCount = results.filter(r => !r.success).length

      if (failCount > 0) {
        const errors = results.filter(r => !r.success).map(r => r.error).join(', ')
        throw new Error(`Failed to create ${failCount} invoice(s): ${errors}`)
      }

      // Show success message with invoice numbers
      const invoiceNumbers = results.map(r => r.xero_invoice_number).join(', ')
      setSuccessMessage(`Successfully created ${successCount} invoice(s) in Xero: ${invoiceNumbers}`)
      setShowSuccessDialog(true)

      // Refresh data to show new invoices
      await refreshData()

      // Close review modal
      setShowReviewModal(false)

    } catch (err) {
      console.error('Error sending invoices to Xero:', err)
      setError(err.message || 'Unable to create invoices in Xero. Please try again or contact support.')
    } finally {
      setLoadingCustomers(prev => ({ ...prev, [customerId]: false }))
    }
  }

  // Obsolete functions removed (Task 5 v2 cleanup):
  // - handleAcceptInvoice: Preview modal removed, direct Xero creation only
  // - handleSeparatePDFGeneration: PDF generation removed, Xero generates PDFs
  // - handleCombinedPDFGeneration: PDF generation removed, Xero generates PDFs

  // eslint-disable-next-line no-unused-vars
  const handlePaidStatusChange = async (invoiceId, currentPaidStatus) => {
    try {
      const newPaidStatus = !currentPaidStatus
      
      // Optimistically update the UI
      setFilteredInvoices(prev => prev.map(invoice => 
        invoice.id === invoiceId 
          ? { ...invoice, paid: newPaidStatus }
          : invoice
      ))
      setAllInvoices(prev => prev.map(invoice => 
        invoice.id === invoiceId 
          ? { ...invoice, paid: newPaidStatus }
          : invoice
      ))

      // Update the database
      await updateInvoicePaidStatus(invoiceId, newPaidStatus)
      
    } catch (err) {
      // Revert the optimistic update on error
      setFilteredInvoices(prev => prev.map(invoice => 
        invoice.id === invoiceId 
          ? { ...invoice, paid: currentPaidStatus }
          : invoice
      ))
      setAllInvoices(prev => prev.map(invoice => 
        invoice.id === invoiceId 
          ? { ...invoice, paid: currentPaidStatus }
          : invoice
      ))
      
      console.error('Failed to update invoice paid status:', err)
      setError('Unable to update payment status. Please try again.')
    }
  }

  const calculateDaysAgo = (dateString) => {
    if (!dateString) return 'Never'
    
    const today = new Date()
    const lastInvoiceDate = parseDateString(dateString)
    const diffTime = today - lastInvoiceDate
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return '1 day ago'
    return `${diffDays} days ago`
  }

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    })
  }

  const renderPaymentMethodBadge = (method) => {
    if (!method) return null

    const badgeConfig = {
      cash: {
        label: 'Cash',
        className: 'bg-gray-100 text-gray-700 border-gray-300'
      },
      xero: {
        label: 'Xero',
        className: 'bg-blue-50 text-blue-700 border-blue-300'
      },
      gocardless: {
        label: 'GoCardless',
        className: 'bg-yellow-50 text-yellow-700 border-yellow-300'
      }
    }

    const config = badgeConfig[method?.toLowerCase()]
    if (!config) return null

    return (
      <Badge variant="outline" className={config.className}>
        {config.label}
      </Badge>
    )
  }

  // Draft Invoice handlers (Task 13)
  const handlePreviewDraft = (draft) => {
    setSelectedDraft(draft)
    setShowDraftPreviewModal(true)
  }

  const handleCloseDraftPreview = () => {
    setShowDraftPreviewModal(false)
    setSelectedDraft(null)
  }

  const handlePreviousDraft = () => {
    if (!selectedDraft) return
    const currentIndex = draftInvoices.findIndex(d => d.id === selectedDraft.id)
    if (currentIndex > 0) {
      setSelectedDraft(draftInvoices[currentIndex - 1])
    }
  }

  const handleNextDraft = () => {
    if (!selectedDraft) return
    const currentIndex = draftInvoices.findIndex(d => d.id === selectedDraft.id)
    if (currentIndex < draftInvoices.length - 1) {
      setSelectedDraft(draftInvoices[currentIndex + 1])
    }
  }

  const handleFetchDraftPDF = async (draft) => {
    setFetchingPdfId(draft.id)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const { data, error } = await supabase.functions.invoke('xero-invoices', {
        body: {
          action: 'fetch-pdf',
          invoiceId: draft.id,
          xeroInvoiceId: draft.xero_invoice_id
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      // Edge function returns 200 even on failure — check response body
      if (data && !data.success) {
        throw new Error(data.message || 'PDF not yet available from Xero')
      }

      // Reload draft invoices to get updated URL
      await loadDraftInvoices()

      // Update selected draft with new data if modal is open
      const updatedDraft = draftInvoices.find(d => d.id === draft.id)
      if (updatedDraft) {
        setSelectedDraft(updatedDraft)
      }
    } catch (err) {
      console.error('Error fetching PDF:', err)
      setError(err.message || 'Failed to fetch PDF from Xero')
    } finally {
      setFetchingPdfId(null)
    }
  }

  const handleAuthoriseDraft = async (draftId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      // Call Edge Function to authorise draft
      const { data, error } = await supabase.functions.invoke('xero-invoices', {
        body: {
          action: 'authorise-and-send',
          invoice_id: draftId
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      // Reload data
      await Promise.all([
        loadDraftInvoices(),
        loadAllInvoices(),
        loadUninvoicedData()
      ])

      setSuccessMessage('Draft invoice authorised and sent successfully!')
      setShowSuccessDialog(true)
      handleCloseDraftPreview()
    } catch (err) {
      console.error('Error authorising draft:', err)
      setError(err.message || 'Failed to authorise draft invoice')
    }
  }

  const handleDeleteDraft = async (draftId) => {
    if (!confirm('Are you sure you want to delete this draft? The sales will return to accruing status.')) {
      return
    }

    try {
      await deleteDraftInvoice(draftId)

      // Reload data
      await Promise.all([
        loadDraftInvoices(),
        loadUninvoicedData()
      ])

      setSuccessMessage('Draft invoice deleted. Sales returned to accruing status.')
      setShowSuccessDialog(true)
    } catch (err) {
      console.error('Error deleting draft:', err)
      setError(err.message || 'Failed to delete draft invoice')
    }
  }

  // Task 14: Draft Invoice Management handlers
  const handleRevertDraftClick = (draft) => {
    setDraftToRevert(draft)
    setShowRevertConfirm(true)
  }

  const handleRevertDraftConfirm = async () => {
    if (!draftToRevert) return

    setIsReverting(true)
    try {
      const result = await revertDraftInvoice(draftToRevert.id)

      // Reload data
      await Promise.all([
        loadDraftInvoices(),
        loadUninvoicedData()
      ])

      // Use the Xero invoice number (INV-XXXX) if available, otherwise fall back to internal draft number
      const displayInvoiceNumber = draftToRevert.xero_mapping?.xero_invoice_number || draftToRevert.invoice_no
      setSuccessMessage(`Draft invoice ${displayInvoiceNumber} reverted. ${result.salesReverted} sales returned to uninvoiced status.`)
      setShowSuccessDialog(true)
      setShowRevertConfirm(false)
      setDraftToRevert(null)
      setSelectedDraftIds(new Set()) // Clear selection
    } catch (err) {
      console.error('Error reverting draft:', err)
      setError(err.message || 'Failed to revert draft invoice')
      setShowRevertConfirm(false)
    } finally {
      setIsReverting(false)
    }
  }

  // Pre-authorise check: compare Odin vs Xero contact data before sending
  const checkContactDataBeforeAuthorise = useCallback(async (invoiceIds) => {
    // Get unique customer IDs from selected drafts
    const selectedDrafts = draftInvoices.filter(d => invoiceIds.includes(d.id))
    const customerIds = [...new Set(selectedDrafts.map(d => d.customer_id).filter(Boolean))]

    // Check each customer for differences
    for (const customerId of customerIds) {
      try {
        const { data, error } = await supabase.functions.invoke('xero-contacts', {
          body: { action: 'compare', customerId }
        })
        if (error) {
          console.warn('Compare check failed, proceeding anyway:', error)
          continue
        }
        if (data?.needsSync && data.differences?.length > 0) {
          // Show diff dialog and pause the flow
          setSyncDiffData(data)
          return data // Return differences to caller
        }
      } catch (err) {
        console.warn('Compare check error, proceeding anyway:', err)
      }
    }
    return null // No differences
  }, [draftInvoices])

  const handleSyncAndProceed = useCallback(async () => {
    if (!syncDiffData || !pendingAuthoriseAction) return
    setSyncDiffSyncing(true)
    try {
      // Sync Odin data to Xero
      const fields = {}
      for (const diff of syncDiffData.differences) {
        if (diff.field === 'Name') fields.Name = diff.odinValue
        if (diff.field === 'Email') fields.EmailAddress = diff.odinValue
        if (diff.field === 'Phone') fields.Phones = [{ PhoneType: 'DEFAULT', PhoneNumber: diff.odinValue }]
      }
      // Address fields
      const addrDiffs = syncDiffData.differences.filter(d =>
        ['Address Line 1', 'City', 'Postcode', 'Country'].includes(d.field)
      )
      if (addrDiffs.length > 0) {
        const addr = { AddressType: 'STREET' }
        for (const d of addrDiffs) {
          if (d.field === 'Address Line 1') addr.AddressLine1 = d.odinValue
          if (d.field === 'City') addr.City = d.odinValue
          if (d.field === 'Postcode') addr.PostalCode = d.odinValue
          if (d.field === 'Country') addr.Country = d.odinValue
        }
        fields.Addresses = [addr]
      }

      if (Object.keys(fields).length > 0) {
        await supabase.functions.invoke('xero-contacts', {
          body: { action: 'update', customerId: syncDiffData.customerId || '', fields }
        })
        toast.success('Xero contact updated')
      }
    } catch (err) {
      console.error('Sync failed:', err)
      toast.error('Failed to sync to Xero — proceeding with authorisation')
    } finally {
      setSyncDiffSyncing(false)
      setShowSyncDiffDialog(false)
      setSyncDiffData(null)
      // Execute the pending authorise action
      pendingAuthoriseAction()
      setPendingAuthoriseAction(null)
    }
  }, [syncDiffData, pendingAuthoriseAction])

  const handleProceedWithoutSync = useCallback(() => {
    setShowSyncDiffDialog(false)
    setSyncDiffData(null)
    if (pendingAuthoriseAction) {
      pendingAuthoriseAction()
      setPendingAuthoriseAction(null)
    }
  }, [pendingAuthoriseAction])

  const handleCancelSync = useCallback(() => {
    setShowSyncDiffDialog(false)
    setSyncDiffData(null)
    setPendingAuthoriseAction(null)
  }, [])

  const handleBatchAuthoriseClick = async () => {
    const selectedIds = Array.from(selectedDraftIds)

    // Pre-authorise check
    const differences = await checkContactDataBeforeAuthorise(selectedIds)
    if (differences) {
      setPendingAuthoriseAction(() => () => {
        setShowBatchAuthoriseConfirm(true)
      })
      setShowSyncDiffDialog(true)
      return
    }

    setShowBatchAuthoriseConfirm(true)
  }

  const handleBatchAuthoriseConfirm = async () => {
    const selectedIds = Array.from(selectedDraftIds)
    setIsAuthorising(true)

    try {
      const result = await authoriseAndSendBatch(selectedIds)

      // Show detailed results modal
      setBatchResults(result)
      setShowBatchResults(true)
      setShowBatchAuthoriseConfirm(false)

      // Reload data
      await Promise.all([
        loadDraftInvoices(),
        loadAllInvoices()
      ])

      setSelectedDraftIds(new Set()) // Clear selection
    } catch (err) {
      console.error('Error batch authorising:', err)
      setError(err.message || 'Failed to authorise invoices')
      setShowBatchAuthoriseConfirm(false)
    } finally {
      setIsAuthorising(false)
    }
  }

  const handleRetryEmailClick = async (invoice) => {
    try {
      const result = await retryInvoiceEmail(invoice.id)

      // Reload draft invoices to update email status
      await loadDraftInvoices()

      setSuccessMessage(`Email ${result.success ? 'sent successfully' : 'failed to send'}. Attempt ${result.attemptCount} of 3.`)
      setShowSuccessDialog(true)
    } catch (err) {
      console.error('Error retrying email:', err)
      setError(err.message || 'Failed to retry email send')
    }
  }

  const handleToggleDraftSelection = (draftId) => {
    setSelectedDraftIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(draftId)) {
        newSet.delete(draftId)
      } else {
        newSet.add(draftId)
      }
      return newSet
    })
  }

  const handleToggleAllDrafts = () => {
    if (selectedDraftIds.size === draftInvoices.length) {
      setSelectedDraftIds(new Set())
    } else {
      setSelectedDraftIds(new Set(draftInvoices.map(d => d.id)))
    }
  }

  const getSelectedDraftsTotalAmount = () => {
    return draftInvoices
      .filter(d => selectedDraftIds.has(d.id))
      .reduce((sum, d) => sum + parseFloat(d.total || 0), 0)
  }

  // Xero sync functions
  // Note: handleXeroSync (standalone sync) was removed — it was never wired to a
  // button. The live path is handleXeroRetry below, which hits the `retry`
  // Edge Function route and internally calls syncInvoiceToXero.
  const handleXeroRetry = async (invoiceId) => {
    try {
      setSyncingInvoices(prev => ({ ...prev, [invoiceId]: true }))

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const { data, error } = await supabase.functions.invoke('xero-invoices/retry', {
        body: { invoiceId },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) throw error

      if (data.success) {
        setSyncStatuses(prev => ({
          ...prev,
          [invoiceId]: {
            status: 'synced',
            xeroInvoiceId: data.xeroInvoiceId,
            onlinePaymentUrl: data.onlinePaymentUrl
          }
        }))

        const updateInvoice = (invoice) => {
          if (invoice.id === invoiceId) {
            return {
              ...invoice,
              xero_sync_status: 'synced',
              xero_invoice_id: data.xeroInvoiceId,
              online_payment_url: data.onlinePaymentUrl
            }
          }
          return invoice
        }

        setAllInvoices(prev => prev.map(updateInvoice))
        setFilteredInvoices(prev => prev.map(updateInvoice))

        console.log('Invoice retry succeeded')
      } else {
        throw new Error(data.error || 'Retry failed')
      }
    } catch (err) {
      console.error('Xero retry error:', err)
      setError(`Failed to retry sync: ${err.message}`)
    } finally {
      setSyncingInvoices(prev => ({ ...prev, [invoiceId]: false }))
    }
  }

  const copyPaymentUrl = (url, invoiceId) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedPaymentUrl(invoiceId)
      setTimeout(() => setCopiedPaymentUrl(null), 2000)
    }).catch(err => {
      console.error('Failed to copy URL:', err)
      setError('Failed to copy payment URL')
    })
  }

  // Email functions
  // eslint-disable-next-line no-unused-vars
  const handleOpenEmailDialog = (invoice) => {
    setSelectedInvoiceForEmail(invoice)
    setEmailDialogOpen(true)
  }

  const handleEmailSent = async () => {
    // Refresh invoice data to show updated email status
    await loadAllInvoices()
    // Close the dialog
    setEmailDialogOpen(false)
    setSelectedInvoiceForEmail(null)
  }

  // Task 12: Mark invoice as paid (US-REC-001: Logic moved to MarkAsPaidDialog component)
  const handleOpenMarkPaidDialog = async (invoice) => {
    // Check if invoice already has a Xero webhook payment
    try {
      const { data: webhookPayments, error } = await supabase
        .from('payments')
        .select('id, amount, date')
        .eq('invoice_id', invoice.id)
        .eq('source', 'xero_webhook')

      if (error) {
        console.error('[InvoiceDashboard] Error checking for webhook payments:', error)
        // Continue anyway - don't block the user
      } else if (webhookPayments && webhookPayments.length > 0) {
        // Invoice already has webhook payment - show toast and don't open dialog
        toast.warning('Payment already recorded', {
          description: 'This invoice has a payment recorded via Xero webhook. Cannot manually mark as paid.'
        })
        return
      }
    } catch (err) {
      console.error('[InvoiceDashboard] Error checking for webhook payments:', err)
      // Continue anyway - don't block the user
    }

    setInvoiceToMarkPaid(invoice)
    setShowMarkPaidDialog(true)
  }

  // Handle successful payment recording from MarkAsPaidDialog
  const handlePaymentSuccess = async () => {
    setSuccessMessage('Payment recorded successfully')
    setShowSuccessDialog(true)
    await refreshData()
  }

  // US-REC-001: Open invoice detail panel
  const handleOpenDetailPanel = (invoice) => {
    setSelectedDetailInvoice(invoice)
    setShowDetailPanel(true)
  }

  // US-REC-001: Close invoice detail panel
  const handleCloseDetailPanel = () => {
    setShowDetailPanel(false)
    setSelectedDetailInvoice(null)
  }

  // US-REC-001: Open credit note modal
  const handleOpenCreditNoteModal = (invoice) => {
    setSelectedCreditNoteInvoice(invoice)
    setShowCreditNoteModal(true)
  }

  // US-REC-001: Handle credit note creation success
  const handleCreditNoteSuccess = async () => {
    toast.success('Credit note created successfully')
    setShowCreditNoteModal(false)
    setSelectedCreditNoteInvoice(null)
    // Refresh data to update balances
    await refreshData()
    // If detail panel is open, close and reopen to refresh data
    if (showDetailPanel && selectedDetailInvoice) {
      const invoiceId = selectedDetailInvoice.id
      handleCloseDetailPanel()
      // Small delay to allow data refresh
      setTimeout(() => {
        const updatedInvoice = allInvoices.find(inv => inv.id === invoiceId)
        if (updatedInvoice) {
          handleOpenDetailPanel(updatedInvoice)
        }
      }, 500)
    }
  }

  // Write off invoice — creates a write_off credit note for the full remaining balance
  const handleOpenWriteOffDialog = (invoice) => {
    setWriteOffInvoice(invoice)
    setWriteOffReason('')
    setShowWriteOffDialog(true)
  }

  const handleConfirmWriteOff = async () => {
    if (!writeOffInvoice) return

    setIsWritingOff(true)
    try {
      const { data, error } = await supabase.functions.invoke('credit-notes', {
        body: {
          action: 'create',
          customer_id: writeOffInvoice.customer_id,
          invoice_id: writeOffInvoice.id,
          amount: parseFloat(writeOffInvoice.amount_due),
          reason: writeOffReason.trim() || 'Small balance written off',
          type: 'write_off',
          date: getTodayISOString()
        }
      })

      if (error) throw new Error(error.message || 'Failed to write off invoice')
      if (data?.error) throw new Error(data.message || 'Failed to write off invoice')

      toast.success(`${formatInvoiceDisplayName(writeOffInvoice)} written off (${formatCurrency(writeOffInvoice.amount_due)})`)
      setShowWriteOffDialog(false)
      setWriteOffInvoice(null)
      await refreshData()
    } catch (err) {
      console.error('[WriteOff] Error:', err)
      toast.error(`Write-off failed: ${err.message}`)
    } finally {
      setIsWritingOff(false)
    }
  }

  const renderEmailStatusBadge = (invoice) => {
    const status = invoice.email_status
    const sentAt = invoice.email_sent_at

    if (status === 'sent' && sentAt) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
          ✓ Sent
        </Badge>
      )
    } else if (status === 'failed') {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
          ⚠ Failed
        </Badge>
      )
    } else if (status === 'pending') {
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
          ⏳ Pending
        </Badge>
      )
    }

    return (
      <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">
        Not Sent
      </Badge>
    )
  }

  const renderXeroSyncBadge = (invoice) => {
    const status = invoice.xero_sync_status || 'pending'
    const isSyncing = syncingInvoices[invoice.id]

    if (isSyncing) {
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
          <div className="flex items-center space-x-1">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-700"></div>
            <span>Syncing...</span>
          </div>
        </Badge>
      )
    }

    switch (status) {
      case 'synced':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
            ✓ Synced
          </Badge>
        )
      case 'failed':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
            ⚠ Error
          </Badge>
        )
      default:
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">
            ⊘ Not Synced
          </Badge>
        )
    }
  }

  const renderXeroSyncControls = (invoice) => {
    const status = invoice.xero_sync_status || 'pending'
    const isSyncing = syncingInvoices[invoice.id]
    const onlinePaymentUrl = invoice.online_payment_url
    const isCustomerMapped = invoice.customer?.xero_contact_id != null

    // If customer is not mapped to Xero, show "Not Mapped" badge
    if (!isCustomerMapped) {
      return (
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="bg-gray-100 text-gray-600">
            Not Mapped
          </Badge>
          <span className="text-xs text-gray-500">
            Map customer in Xero settings
          </span>
        </div>
      )
    }

    return (
      <div className="flex items-center space-x-2">
        {renderXeroSyncBadge(invoice)}

        {/* Automatic sync - no manual toggle needed */}
        {/* Sync happens automatically after invoice generation */}

        {status === 'failed' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleXeroRetry(invoice.id)}
            disabled={isSyncing}
          >
            Retry
          </Button>
        )}

        {status === 'synced' && onlinePaymentUrl && (
          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyPaymentUrl(onlinePaymentUrl, invoice.id)}
            >
              {copiedPaymentUrl === invoice.id ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(onlinePaymentUrl, '_blank')}
            >
              View
            </Button>
          </div>
        )}
      </div>
    )
  }

  // Sorting functions
  const handleUninvoicedSort = (key) => {
    let direction = 'asc'
    if (uninvoicedSortConfig.key === key && uninvoicedSortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setUninvoicedSortConfig({ key, direction })

    // Flatten the data into customer+branch rows for sorting
    const flatRows = customersWithUninvoiced.flatMap(customerGroup =>
      customerGroup.branches.map(branch => ({
        customer: customerGroup.customer,
        branch
      }))
    )

    // Sort the flattened rows
    const sortedRows = [...flatRows].sort((a, b) => {
      let aVal, bVal

      switch (key) {
        case 'name':
          aVal = a.customer.name.toLowerCase()
          bVal = b.customer.name.toLowerCase()
          break
        case 'branch':
          aVal = a.branch.name.toLowerCase()
          bVal = b.branch.name.toLowerCase()
          break
        case 'type':
          aVal = (a.branch.customer_type?.name || '').toLowerCase()
          bVal = (b.branch.customer_type?.name || '').toLowerCase()
          break
        case 'total':
          aVal = parseFloat(a.branch.total)
          bVal = parseFloat(b.branch.total)
          break
        case 'lastInvoiced':
          aVal = lastInvoiceDates[a.customer.id] ? new Date(lastInvoiceDates[a.customer.id]) : new Date(0)
          bVal = lastInvoiceDates[b.customer.id] ? new Date(lastInvoiceDates[b.customer.id]) : new Date(0)
          break
        default:
          return 0
      }

      if (aVal < bVal) {
        return direction === 'asc' ? -1 : 1
      }
      if (aVal > bVal) {
        return direction === 'asc' ? 1 : -1
      }
      return 0
    })

    // Group sorted rows back into customer groups for state
    const groupedData = {}
    sortedRows.forEach(row => {
      const customerId = row.customer.id
      if (!groupedData[customerId]) {
        groupedData[customerId] = {
          customer: row.customer,
          branches: []
        }
      }
      groupedData[customerId].branches.push(row.branch)
    })

    setCustomersWithUninvoiced(Object.values(groupedData))
  }

  const handleInvoicesSort = (key) => {
    let direction = 'asc'
    if (invoicesSortConfig.key === key && invoicesSortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setInvoicesSortConfig({ key, direction })

    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
      let aVal, bVal

      switch (key) {
        case 'date':
          aVal = new Date(a.date)
          bVal = new Date(b.date)
          break
        case 'invoice_no':
          aVal = a.invoice_no.toLowerCase()
          bVal = b.invoice_no.toLowerCase()
          break
        case 'customer':
          aVal = (a.customer?.name || '').toLowerCase()
          bVal = (b.customer?.name || '').toLowerCase()
          break
        case 'branch':
          aVal = (a.branch?.name || 'main').toLowerCase()
          bVal = (b.branch?.name || 'main').toLowerCase()
          break
        case 'total':
          aVal = parseFloat(a.total) || 0
          bVal = parseFloat(b.total) || 0
          break
        case 'paid':
          aVal = a.paid ? 1 : 0
          bVal = b.paid ? 1 : 0
          break
        default:
          return 0
      }

      if (aVal < bVal) {
        return direction === 'asc' ? -1 : 1
      }
      if (aVal > bVal) {
        return direction === 'asc' ? 1 : -1
      }
      return 0
    })

    setFilteredInvoices(sortedInvoices)
    // Also update allInvoices if we're not filtering
    if (searchTerm.trim() === '') {
      setAllInvoices(sortedInvoices)
    }
  }

  const getSortIcon = (key, sortConfig) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }

  if (loading && customersWithUninvoiced.length === 0 && allInvoices.length === 0) {
    return (
        <PageLayout
          title="Invoicing"
          user={user}
          onLogout={onLogout}
        >
          <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-lg text-muted-foreground">Loading invoice dashboard...</p>
          </div>
        </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Invoicing"
      user={user}
      onLogout={onLogout}
      className="invoice-dashboard-page"
    >
      <div className="h-full overflow-y-auto">
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
          </div>
        )}

        {/* Customers with Uninvoiced Sales Section */}
        <div className="sales-form-container">
          <div className="table-header">
            <h3>Accruing Invoices</h3>
          </div>

          {customersWithUninvoiced.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="text-lg font-semibold mb-2">You're up to date!</h3>
              <p className="text-gray-600">All invoices have been generated. There are no customers with accruing deliveries at this time.</p>
            </div>
          ) : (
            <TooltipProvider>
            {/* Mobile cards for Accruing Invoices */}
            <div className="mobile-invoice-cards md:hidden">
              {flattenedUninvoicedRows.map(row => {
                const loadingKey = `${row.customer.id}-${row.branch.id}`
                return (
                  <div
                    key={loadingKey}
                    className="mobile-accruing-card"
                    onClick={() => handleReviewTransactions(row.customer.id, row.branch.id)}
                  >
                    <div className="mobile-card-header">
                      <div className="mobile-card-customer">
                        <div className="mobile-card-customer-name flex items-center gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCustomerClick(row.customer) }}
                            className="text-left hover:text-primary hover:underline transition-colors"
                          >
                            {row.customer.name}
                          </button>
                          {getCustomerWarnings(row).length > 0 && (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                          )}
                        </div>
                        <div className="mobile-card-customer-type">
                          {row.branch.customer_type?.name || 'N/A'}
                        </div>
                      </div>
                      <div className="mobile-card-total">
                        {formatCurrency(row.branch.total)}
                      </div>
                    </div>
                    <div className="mobile-card-branch">{row.branch.name}</div>
                    <div className="mobile-card-footer">
                      <span className="mobile-card-last-invoiced">
                        Last invoiced: {calculateDaysAgo(lastInvoiceDates[row.customer.id])}
                      </span>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleReviewTransactions(row.customer.id, row.branch.id)
                        }}
                        disabled={loadingCustomers[loadingKey]}
                      >
                        {loadingCustomers[loadingKey] ? 'Loading...' : 'Review'}
                      </Button>
                    </div>
                  </div>
                )
              })}
              <div className="flex justify-between items-center px-4 py-3 border-t font-semibold">
                <span>Total:</span>
                <span className="font-mono font-bold">
                  {formatCurrency(flattenedUninvoicedRows.reduce((sum, row) => sum + parseFloat(row.branch.total || 0), 0))}
                </span>
              </div>
            </div>

            {/* Desktop table for Accruing Invoices */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleUninvoicedSort('name')} className="cursor-pointer hover:bg-muted/50">
                    Customer {getSortIcon('name', uninvoicedSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleUninvoicedSort('branch')} className="cursor-pointer hover:bg-muted/50">
                    Branch {getSortIcon('branch', uninvoicedSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleUninvoicedSort('total')} className="cursor-pointer hover:bg-muted/50">
                    Total {getSortIcon('total', uninvoicedSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleUninvoicedSort('lastInvoiced')} className="cursor-pointer hover:bg-muted/50">
                    Last Invoiced {getSortIcon('lastInvoiced', uninvoicedSortConfig)}
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flattenedUninvoicedRows.map(row => {
                  const loadingKey = `${row.customer.id}-${row.branch.id}`
                  return (
                    <TableRow key={loadingKey}>
                      <TableCell className="font-semibold">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleCustomerClick(row.customer)}
                            className="text-left hover:text-primary hover:underline transition-colors"
                          >
                            {row.customer.name}
                          </button>
                          {getCustomerWarnings(row).length > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                <ul className="text-sm space-y-1">
                                  {getCustomerWarnings(row).map((w, i) => (
                                    <li key={i}>{w}</li>
                                  ))}
                                </ul>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {row.branch.customer_type?.name || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{row.branch.name}</span>
                      </TableCell>
                      <TableCell className="font-mono font-semibold">
                        {formatCurrency(row.branch.total)}
                      </TableCell>
                      <TableCell>
                        {calculateDaysAgo(lastInvoiceDates[row.customer.id])}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleReviewTransactions(row.customer.id, row.branch.id)}
                          disabled={loadingCustomers[loadingKey]}
                        >
                          {loadingCustomers[loadingKey] ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Loading...
                            </>
                          ) : (
                            'Review'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} className="text-right font-semibold">Total:</TableCell>
                  <TableCell className="font-mono font-bold">
                    {formatCurrency(flattenedUninvoicedRows.reduce((sum, row) => sum + parseFloat(row.branch.total || 0), 0))}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
            </TooltipProvider>
          )}
        </div>

        {/* Draft Invoices Section (Task 13, enhanced Task 14) */}
        {draftInvoices.length > 0 && <TooltipProvider>
          <div className="sales-form-container">
            <div className="table-header">
              <h3>Draft Invoices</h3>
            {draftInvoices.length > 0 && (
              <Button
                onClick={handleBatchAuthoriseClick}
                disabled={selectedDraftIds.size === 0 || selectedDraftIds.size > 20}
                className="ml-auto"
              >
                {selectedDraftIds.size > 0
                  ? `Authorise & Send Selected (${selectedDraftIds.size})`
                  : 'Authorise & Send Selected'}
              </Button>
            )}
          </div>

            {/* Mobile cards for Draft Invoices */}
            <div className="mobile-invoice-cards md:hidden">
              {draftInvoices.map((draft) => (
                <div key={draft.id} className="mobile-draft-card">
                  <div className="mobile-card-header">
                    <div className="mobile-card-checkbox">
                      <Checkbox
                        checked={selectedDraftIds.has(draft.id)}
                        onCheckedChange={() => handleToggleDraftSelection(draft.id)}
                        aria-label={`Select invoice ${draft.xero_mapping?.xero_invoice_number || draft.invoice_no}`}
                      />
                    </div>
                    <div className="mobile-card-info">
                      <div className="mobile-card-invoice-no">
                        {draft.xero_mapping?.xero_invoice_number || draft.invoice_no}
                      </div>
                      <div className="mobile-card-date">{formatDate(draft.date)}</div>
                    </div>
                    <div className="mobile-card-amount">
                      {formatCurrency(draft.total)}
                    </div>
                  </div>
                  <div className="mobile-card-customer">
                    {draft.customer ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCustomerClick(draft.customer) }}
                        className="text-left hover:text-primary hover:underline transition-colors"
                      >
                        {draft.customer.name}
                      </button>
                    ) : 'Unknown'}
                  </div>
                  <div className="mobile-card-branch">{draft.branch?.name || 'Main'}</div>
                  <div className="mobile-card-footer">
                    <div className="mobile-card-payment">
                      {renderPaymentMethodBadge(draft.payment_method)}
                    </div>
                    <div className="mobile-card-actions">
                      {draft.emailFailed && (
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      )}
                      {draft.xero_pdf_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreviewDraft(draft)}
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        onClick={() => handleRevertDraftClick(draft)}
                        title="Revert"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table for Draft Invoices */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedDraftIds.size === draftInvoices.length && draftInvoices.length > 0}
                      onCheckedChange={handleToggleAllDrafts}
                      aria-label="Select all drafts"
                    />
                  </TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Email Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draftInvoices.map((draft) => (
                  <TableRow key={draft.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedDraftIds.has(draft.id)}
                        onCheckedChange={() => handleToggleDraftSelection(draft.id)}
                        aria-label={`Select invoice ${draft.xero_mapping?.xero_invoice_number || draft.invoice_no}`}
                      />
                    </TableCell>
                    <TableCell>{formatDate(draft.date)}</TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {draft.xero_mapping?.xero_invoice_number || draft.invoice_no}
                      </span>
                    </TableCell>
                    <TableCell>
                      {draft.customer ? (
                        <button
                          onClick={() => handleCustomerClick(draft.customer)}
                          className="text-left hover:text-primary hover:underline transition-colors"
                        >
                          {draft.customer.name}
                        </button>
                      ) : 'Unknown'}
                    </TableCell>
                    <TableCell>{draft.branch?.name || 'Main'}</TableCell>
                    <TableCell className="font-mono font-semibold">
                      {formatCurrency(draft.total)}
                    </TableCell>
                    <TableCell>
                      {renderPaymentMethodBadge(draft.payment_method)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {draft.emailFailed && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help inline-flex">
                                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>This invoice was authorised but failed to send. Please check the email is correct or contact Support.</p>
                              </TooltipContent>
                            </Tooltip>
                            {draft.canRetryEmail && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 h-auto"
                                onClick={() => handleRetryEmailClick(draft)}
                                title="Try send again"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {draft.xero_pdf_url ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePreviewDraft(draft)}
                          >
                            Preview
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFetchDraftPDF(draft)}
                            disabled={fetchingPdfId === draft.id}
                            title="Fetch PDF from Xero"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${fetchingPdfId === draft.id ? 'animate-spin' : ''}`} />
                            {fetchingPdfId === draft.id ? 'Fetching...' : 'Retry PDF'}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-2"
                          onClick={() => handleRevertDraftClick(draft)}
                          title="Revert to Draft - Returns sales to uninvoiced status"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>}

        {/* Invoices Section */}
        <div className="sales-form-container invoice-dashboard">
          {/* Table Header with Invoices title and Outstanding tally */}
          <div className="table-header">
            <h3>Invoices</h3>
            <div className="outstanding-tally">
              Outstanding: £{filteredInvoices
                .filter(inv => inv.calculated_status !== 'paid' && inv.calculated_status !== 'voided')
                .reduce((sum, inv) => sum + (parseFloat(inv.amount_due) || 0), 0)
                .toFixed(2)}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="invoices-filters-row">
            <Tabs value={activeFilter} onValueChange={setActiveFilter}>
              <TabsList className="h-10 p-1 bg-gray-100 [&_[data-state=active]]:bg-white">
                <TabsTrigger value="all">
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">All</span>
                </TabsTrigger>
                <TabsTrigger value="gocardless">
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1.5">
                          <svg className="h-4 w-4" viewBox="0 0 1000 1000" fill="currentColor">
                            <circle cx="500" cy="500" r="500" fill="#F1F252"/>
                            <path fill="#1C1B18" d="M507.9,242.1c55.2,0,86.2,9,86.2,9l91.7,187.2l-0.8,0.8l-118-70.4c-68.4-40.7-118-62.1-158.4-60.5 c-42.7,0.8-68.3,35.2-68.3,85c1.5,127.5,122.7,284.5,243.1,284.5c49.1,0,74.7-15.8,89.7-34.9L494.8,447.3v-0.8h244.8 c3.3,17.5,5.2,35.3,5.4,53.1c0,143.1-109.5,258.3-244.6,258.3C364.7,757.9,255,642.7,255,499.6C254.8,357.3,364.3,242.1,507.9,242.1z"/>
                          </svg>
                          <span className="hidden sm:inline">GoCardless</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {nextPayoutLoading
                          ? 'Loading...'
                          : nextPayout?.payout === null
                            ? 'No pending payouts'
                            : nextPayout?.amount != null
                              ? `Next payout: £${nextPayout.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · Arrives ${format(parseISO(nextPayout.arrival_date), 'd MMM yy')}`
                              : 'No pending payouts'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TabsTrigger>
                <TabsTrigger value="xero">
                  <svg className="h-4 w-4" viewBox="0 0 256 256" fill="currentColor">
                    <circle cx="128" cy="128" r="128" fill="#1FC0E7"/>
                    <path fill="#FFFFFF" d="M62.37,127.97L84.17,106.07c0.72-0.73,1.13-1.71,1.13-2.74c0-2.16-1.75-3.91-3.91-3.91c-1.05,0-2.04,0.41-2.78,1.17L56.82,122.38L34.95,100.54c-0.74-0.73-1.72-1.13-2.76-1.13c-2.16,0-3.91,1.75-3.91,3.91c0,1.05,0.42,2.05,1.17,2.79L51.25,127.93L29.46,149.78c-0.76,0.76-1.18,1.75-1.18,2.8c0,2.16,1.75,3.91,3.91,3.91c1.04,0,2.02-0.4,2.76-1.14L56.79,133.5L78.55,155.27c0.77,0.8,1.76,1.23,2.83,1.23c2.15,0,3.9-1.75,3.9-3.91c0-1.04-0.4-2.02-1.14-2.76L62.37,127.95V127.97zM191.97,127.96c0,3.93,3.19,7.11,7.11,7.11c3.9,0,7.09-3.19,7.09-7.11c0-3.93-3.19-7.11-7.09-7.11c-3.91,0-7.09,3.19-7.09,7.11H191.97zM178.51,127.96c0-11.35,9.22-20.59,20.56-20.59c11.32,0,20.55,9.24,20.55,20.59c0,11.35-9.23,20.59-20.55,20.59c-11.34,0-20.56-9.23-20.56-20.59H178.51zM170.42,127.96c0,15.82,12.86,28.69,28.65,28.69c15.79,0,28.65-12.87,28.65-28.69c0-15.82-12.86-28.69-28.65-28.69c-15.8,0-28.65,12.87-28.65,28.69H170.42zM168.39,99.76h-1.19c-3.61,0-7.1,1.14-10,3.39c-0.4-1.74-1.97-3.07-3.84-3.07c-2.14,0-3.87,1.74-3.87,3.89l0.01,48.33c0,2.14,1.76,3.89,3.9,3.89c2.15,0,3.9-1.75,3.91-3.9v-29.72c0-9.9,0.91-13.9,9.38-14.96c0.8-0.1,1.64-0.08,1.65-0.08c2.31-0.09,3.96-1.68,3.96-3.84c0-2.16-1.76-3.91-3.93-3.91L168.39,99.76zM93.38,123.25c0-0.11,0.01-0.23,0.02-0.33c2.26-8.99,10.39-15.63,20.05-15.63c9.79,0,17.98,6.79,20.14,15.93L93.37,123.23L93.38,123.25zM141.58,122.51c-1.68-7.98-6.05-14.54-12.69-18.75c-9.72-6.18-22.54-5.84-31.92,0.87c-7.66,5.44-12.08,14.36-12.08,23.55c0,2.3,0.28,4.63,0.85,6.92c2.89,11.38,12.66,20,24.32,21.43c3.46,0.42,6.83,0.23,10.3-0.69c3-0.74,5.9-1.94,8.57-3.66c2.77-1.78,5.09-4.14,7.34-6.95c0.03-0.06,0.08-0.1,0.13-0.16c1.56-1.93,1.27-4.7-0.44-6.01c-1.44-1.11-3.87-1.56-5.77,0.89c-0.41,0.59-0.87,1.19-1.37,1.79c-1.52,1.68-3.4,3.3-5.66,4.56c-2.88,1.54-6.15,2.41-9.62,2.43c-11.37-0.13-17.44-8.07-19.61-13.73c-0.38-1.06-0.67-2.16-0.87-3.3l-0.06-0.6h40.79c5.59-0.13,8.59-4.08,7.76-8.6L141.58,122.51z"/>
                  </svg>
                  <span className="hidden sm:inline">Xero</span>
                </TabsTrigger>
                <TabsTrigger value="paid">
                  <CircleCheck className="h-4 w-4 text-green-600" />
                  <span className="hidden sm:inline">Paid</span>
                </TabsTrigger>
                <TabsTrigger value="unpaid">
                  <Circle className="h-4 w-4 text-gray-400" />
                  <span className="hidden sm:inline">Unpaid</span>
                </TabsTrigger>
                <TabsTrigger value="partially_paid">
                  <svg className="h-4 w-4 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 2a10 10 0 0 1 0 20" fill="currentColor" />
                  </svg>
                  <span className="hidden sm:inline">Partial</span>
                </TabsTrigger>
                <TabsTrigger value="overdue">
                  <CircleAlert className="h-4 w-4 text-red-600" />
                  <span className="hidden sm:inline">Overdue</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="search-box-wrapper">
              <SearchInput
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {filteredInvoices.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">No invoices yet</h3>
              <p className="text-gray-600">
                All your invoices (including historical pre-Xero invoices) will appear here.
              </p>
            </div>
          ) : (
            <>
            {/* Mobile cards for Invoices - US-REC-001: Updated to use calculated_status */}
            <div className="mobile-invoice-cards md:hidden">
              {filteredInvoices.map((invoice) => {
                // Calculate days overdue (US-REC-001: Use calculated_status)
                let daysOverdue = null
                if (invoice.calculated_status !== 'paid' && invoice.calculated_status !== 'voided' && invoice.customer?.payment_terms) {
                  const dueDate = addDays(parseISO(invoice.date), parseInt(invoice.customer.payment_terms, 10))
                  const overdue = differenceInDays(new Date(), dueDate)
                  if (overdue > 0) {
                    daysOverdue = overdue
                  }
                }

                return (
                  <div
                    key={invoice.id}
                    className="mobile-invoice-card"
                    onClick={() => handleOpenDetailPanel(invoice)}
                  >
                    <div className="mobile-card-header">
                      <span className="mobile-card-invoice-no">
                        {invoice.xero_mapping?.[0]?.xero_invoice_number || invoice.invoice_no || 'Unknown'}
                      </span>
                      <span className="mobile-card-amount">
                        {formatCurrency(invoice.total)}
                      </span>
                    </div>
                    <div className="mobile-card-customer">{invoice.customer?.name || 'Unknown'}</div>
                    <div className="mobile-card-branch">{invoice.branch?.name || 'Main'}</div>
                    <div className="mobile-card-meta">
                      <span>{formatDate(invoice.date)}</span>
                      {invoice.calculated_status !== 'paid' && invoice.calculated_status !== 'voided' && (
                        <span className="text-muted-foreground">
                          Due: {formatCurrency(invoice.amount_due)}
                        </span>
                      )}
                      {daysOverdue !== null && (
                        <span className="text-red-600 font-medium">{daysOverdue}d overdue</span>
                      )}
                    </div>
                    <div className="mobile-card-footer">
                      <div className="mobile-card-status">
                        {invoice.xero_sync_status === 'failed' ? (
                          <>
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-red-600">Sync Failed</span>
                          </>
                        ) : (
                          <InvoiceStatusBadge
                            status={invoice.calculated_status}
                            gcPaymentStatus={invoice.gc_payment_status}
                          />
                        )}
                      </div>
                      {invoice.calculated_status !== 'paid' && invoice.calculated_status !== 'voided' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenMarkPaidDialog(invoice)
                          }}
                        >
                          Record Payment
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Desktop table for Invoices - US-REC-001: Added Amount Due, Status badge, Actions */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleInvoicesSort('invoice_no')} className="cursor-pointer hover:bg-muted/50">
                    Invoice # {getSortIcon('invoice_no', invoicesSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleInvoicesSort('date')} className="cursor-pointer hover:bg-muted/50">
                    Date {getSortIcon('date', invoicesSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleInvoicesSort('customer')} className="cursor-pointer hover:bg-muted/50">
                    Customer {getSortIcon('customer', invoicesSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleInvoicesSort('branch')} className="cursor-pointer hover:bg-muted/50">
                    Branch {getSortIcon('branch', invoicesSortConfig)}
                  </TableHead>
                  <TableHead onClick={() => handleInvoicesSort('total')} className="cursor-pointer hover:bg-muted/50">
                    Total {getSortIcon('total', invoicesSortConfig)}
                  </TableHead>
                  <TableHead className="text-right">
                    Amount Due
                  </TableHead>
                  <TableHead className="text-center">
                    Days Overdue
                  </TableHead>
                  <TableHead onClick={() => handleInvoicesSort('paid')} className="cursor-pointer hover:bg-muted/50">
                    Status {getSortIcon('paid', invoicesSortConfig)}
                  </TableHead>
                  <TableHead className="text-center">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consolidatedInvoices.map((item, itemIndex) => {
                  const invoice = item.invoice
                  const xeroUrl = invoice.xero_invoice_id ? buildXeroInvoiceUrl(xeroTenantId, invoice.xero_invoice_id) : null

                  // Calculate days overdue for this specific invoice (US-REC-001: Use calculated_status)
                  let daysOverdue = null
                  if (invoice.calculated_status !== 'paid' && invoice.calculated_status !== 'voided' && invoice.customer?.payment_terms) {
                    const dueDate = addDays(parseISO(invoice.date), parseInt(invoice.customer.payment_terms, 10))
                    const overdue = differenceInDays(new Date(), dueDate)
                    if (overdue > 0) {
                      daysOverdue = overdue
                    }
                  }

                  return (
                    <TableRow
                      key={invoice.id}
                      className={`invoice-sub-row ${item.showCustomer && itemIndex > 0 ? 'border-t-2 border-gray-300' : ''}`}
                    >
                      {/* Invoice # */}
                      <TableCell>
                        <Button
                          variant="link"
                          className="p-0 h-auto font-medium text-left text-sm"
                          onClick={() => {
                            setSelectedInvoice(invoice)
                            setShowInvoiceModal(true)
                          }}
                        >
                          {invoice.xero_mapping?.[0]?.xero_invoice_number || invoice.invoice_no || 'Unknown'}
                        </Button>
                      </TableCell>

                      {/* Date - show when date changes from previous row */}
                      <TableCell className="invoice-group-cell">
                        {item.showDate ? formatDate(item.date) : ''}
                      </TableCell>

                      {/* Customer - show when customer changes from previous row */}
                      <TableCell className="invoice-group-cell text-gray-500">
                        {item.showCustomer ? (item.customer?.name || 'Unknown') : ''}
                      </TableCell>

                      {/* Branch name */}
                      <TableCell className="text-gray-700">
                        {invoice.branch?.name || 'Main'}
                      </TableCell>

                      {/* Total */}
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(invoice.total)}
                      </TableCell>

                      {/* Amount Due - US-REC-001 */}
                      <TableCell className="font-mono text-sm text-right">
                        {invoice.calculated_status === 'paid' || invoice.calculated_status === 'voided' ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <span className={parseFloat(invoice.amount_due) < parseFloat(invoice.total) ? 'text-orange-600 font-medium' : ''}>
                            {formatCurrency(invoice.amount_due)}
                          </span>
                        )}
                      </TableCell>

                      {/* Days Overdue */}
                      <TableCell className="text-center">
                        {daysOverdue !== null ? (
                          <span className={daysOverdue >= 8 ? 'text-red-600 font-semibold text-sm' : 'text-orange-500 font-semibold text-sm'}>
                            {daysOverdue}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>

                      {/* Status badge - US-REC-001: Using InvoiceStatusBadge component */}
                      <TableCell>
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            {invoice.xero_sync_status === 'failed' ? (
                              <>
                                <XCircle className="h-4 w-4 text-red-600" aria-label="Failed" />
                                <span className="text-red-600 text-sm font-medium">Sync Failed</span>
                              </>
                            ) : (
                              <>
                                <InvoiceStatusBadge
                                  status={invoice.calculated_status}
                                  gcPaymentStatus={invoice.gc_payment_status}
                                />
                                {(invoice.payment_method === 'gocardless' || invoice.payment_source === 'gocardless') && (
                                  <img
                                    src="/assets/GoCardless Logo assets/02 Symbol/Primary/GoCardless-Symbol-Primary-RGB-500px.svg"
                                    alt="GoCardless"
                                    style={{ width: '14px', height: '14px' }}
                                    title="GoCardless"
                                  />
                                )}
                                {invoice.xero_invoice_id && (
                                  <img
                                    src="/assets/Xero Logo assets/xero-circle.svg"
                                    alt="Xero"
                                    style={{ width: '14px', height: '14px' }}
                                    title="Xero"
                                  />
                                )}
                              </>
                            )}
                          </div>
                          {invoice.xero_sync_status === 'failed' && invoice.xero_error_message && (
                            <span className="text-xs text-red-600">
                              {invoice.xero_error_message}
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Actions dropdown - US-REC-001 */}
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open actions menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem onClick={() => handleOpenDetailPanel(invoice)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {invoice.calculated_status !== 'paid' && invoice.calculated_status !== 'voided' && invoice.calculated_status !== 'written_off' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleOpenMarkPaidDialog(invoice)}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Record Payment
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenCreditNoteModal(invoice)}>
                                  <ReceiptIcon className="h-4 w-4 mr-2" />
                                  Create Credit Note
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleOpenWriteOffDialog(invoice)}>
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Write Off
                                </DropdownMenuItem>
                              </>
                            )}
                            {xeroUrl && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => window.open(xeroUrl, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View in Xero
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </>
          )}
        </div>
      </div>

      {/* Review Modal (Task 5 v2: Direct Xero creation, no preview) */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        reviewData={reviewData}
        customerName={reviewCustomerName}
        customerEmail={reviewCustomerEmail}
        customerBranches={reviewCustomerBranches}
        selectedBranchIds={reviewSelectedBranchIds}
        onBranchSelectionChange={setReviewSelectedBranchIds}
        onGenerateInvoice={handleGenerateInvoiceFromReview}
        onDisregardSale={handleDisregardSale}
        onDraftCreated={loadDraftInvoices}
        onUninvoicedDataRefresh={loadUninvoicedData}
        onDataRefresh={refreshData}
        onMarkAsPaid={async (saleIds) => {
          await markSalesAsPaidWithoutInvoice(saleIds)
          toast.success(`${saleIds.length} sale${saleIds.length !== 1 ? 's' : ''} marked as paid`)
          await loadUninvoicedData()
          setShowReviewModal(false)
        }}
        onCustomerClick={(customer) => handleCustomerClick(customer)}
        onEmailWarning={(custId, errorMsg) => {
          setEmailWarningCustomerId(custId)
          setEmailWarningMessage(errorMsg)
          setShowEmailWarning(true)
        }}
      />

      {/* Email Warning Dialog - invoice authorised but email failed */}
      <AlertDialog open={showEmailWarning} onOpenChange={setShowEmailWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Email Not Sent</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              Invoice authorised successfully, but the email could not be sent.
              {'\n\n'}Reason: {emailWarningMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not Now</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setShowEmailWarning(false)
              if (emailWarningCustomerId) {
                handleCustomerClick({ id: emailWarningCustomerId })
              }
            }}>
              Update Customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invoice Preview Modal removed - Xero-first approach (Task 5 v2) */}

      {/* Historical Invoice Modal */}
      {showInvoiceModal && selectedInvoice && (
        <HistoricalInvoiceModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceModal(false)
            setSelectedInvoice(null)
          }}
          onNavigate={(direction) => {
            const currentIndex = filteredInvoices.findIndex(inv => inv.id === selectedInvoice.id)
            if (direction === 'previous' && currentIndex > 0) {
              setSelectedInvoice(filteredInvoices[currentIndex - 1])
            } else if (direction === 'next' && currentIndex < filteredInvoices.length - 1) {
              setSelectedInvoice(filteredInvoices[currentIndex + 1])
            }
          }}
          currentIndex={filteredInvoices.findIndex(inv => inv.id === selectedInvoice.id)}
          totalInvoices={filteredInvoices.length}
        />
      )}

      {/* Send Invoice Email Dialog */}
      {emailDialogOpen && selectedInvoiceForEmail && (
        <SendInvoiceEmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          invoice={selectedInvoiceForEmail}
          customer={selectedInvoiceForEmail.customer}
          onEmailSent={handleEmailSent}
        />
      )}

      {/* Task 12 / US-REC-001: Mark as Paid Dialog - Enhanced with partial payment support */}
      <MarkAsPaidDialog
        invoice={invoiceToMarkPaid}
        open={showMarkPaidDialog}
        onOpenChange={setShowMarkPaidDialog}
        onSuccess={handlePaymentSuccess}
      />

      {/* US-REC-001: Invoice Detail Panel (slide-over) */}
      <InvoiceDetailPanel
        invoice={selectedDetailInvoice}
        open={showDetailPanel}
        onClose={handleCloseDetailPanel}
        onRecordPayment={(invoice) => {
          handleCloseDetailPanel()
          handleOpenMarkPaidDialog(invoice)
        }}
        onCreateCreditNote={(invoice) => {
          handleCloseDetailPanel()
          handleOpenCreditNoteModal(invoice)
        }}
        onDataRefresh={refreshData}
      />

      {/* US-REC-001: Credit Note Modal */}
      <CreditNoteModal
        invoice={selectedCreditNoteInvoice}
        open={showCreditNoteModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreditNoteModal(false)
            setSelectedCreditNoteInvoice(null)
          }
        }}
        onSuccess={handleCreditNoteSuccess}
      />

      {/* Write Off Confirmation Dialog */}
      <AlertDialog open={showWriteOffDialog} onOpenChange={(open) => {
        if (!open && !isWritingOff) {
          setShowWriteOffDialog(false)
          setWriteOffInvoice(null)
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Write Off Invoice</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will write off the remaining balance of <span className="font-semibold text-foreground">{writeOffInvoice && formatCurrency(writeOffInvoice.amount_due)}</span> on <span className="font-semibold text-foreground">{writeOffInvoice && formatInvoiceDisplayName(writeOffInvoice)}</span>.
                </p>
                <p>A write-off credit note will be created and synced to Xero.</p>
                <div>
                  <label htmlFor="write-off-reason" className="text-sm font-medium text-foreground block mb-1.5">Reason (optional)</label>
                  <Input
                    id="write-off-reason"
                    placeholder="e.g. Small balance, not worth chasing"
                    value={writeOffReason}
                    onChange={(e) => setWriteOffReason(e.target.value)}
                    disabled={isWritingOff}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isWritingOff}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleConfirmWriteOff()
              }}
              disabled={isWritingOff}
              className="bg-red-600 hover:bg-red-700"
            >
              {isWritingOff ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Writing off…
                </>
              ) : (
                'Write Off'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog (Task 5.1: Replace native alert) */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Success
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {successMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowSuccessDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Draft Preview Modal (Task 13) */}
      <Dialog open={showDraftPreviewModal} onOpenChange={(open) => !open && handleCloseDraftPreview()}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedDraft ? (
                <>
                  {selectedDraft.xero_mapping?.xero_invoice_number || selectedDraft.invoice_no} - {selectedDraft.customer?.name}{selectedDraft.branch ? ` - ${selectedDraft.branch.name}` : ''}
                </>
              ) : (
                'Draft Invoice Preview'
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedDraft && (
            <div className="flex-1 flex flex-col min-h-0">
              {/* PDF Preview */}
              {selectedDraft.xero_pdf_url ? (
                <div className="flex-1 flex flex-col">
                  {pdfLoading && (
                      <div className="bg-white border border-gray-200 rounded p-8 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                          <p className="text-sm text-gray-600">Loading PDF preview...</p>
                        </div>
                      </div>
                    )}

                    {pdfError && (
                      <div className="bg-white border border-red-200 rounded p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-red-900">Could not load PDF preview</p>
                            <p className="text-xs text-red-700 mt-1">{pdfError}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {pdfBlobUrl && !pdfLoading && !pdfError && (
                      <div className="bg-white border border-gray-200 rounded overflow-hidden flex-1 flex">
                        <iframe
                          src={`${pdfBlobUrl}#toolbar=0`}
                          className="w-full h-full"
                          title={`Invoice ${selectedDraft.xero_invoice_number} Preview`}
                        />
                      </div>
                    )}
                </div>
              ) : (
                  <div className="bg-white border border-gray-200 rounded p-4 text-center">
                    <p className="text-sm text-gray-600 mb-2">
                      PDF will be available in 15-30 seconds once Xero processes the draft.
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      Click the button below to check when ready.
                    </p>
                    <Button
                      onClick={() => handleFetchDraftPDF(selectedDraft)}
                      variant="outline"
                    >
                      Check for PDF
                    </Button>
                  </div>
                )}
            </div>
          )}

          <DialogFooter className="relative">
            <div className="flex items-center w-full">
              <Button variant="outline" onClick={handleCloseDraftPreview}>
                Close
              </Button>

              {selectedDraft && draftInvoices.length > 1 && (
                <div className="flex items-center gap-3 absolute left-1/2 -translate-x-1/2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousDraft}
                    disabled={draftInvoices.findIndex(d => d.id === selectedDraft.id) === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-500 px-2">
                    {draftInvoices.findIndex(d => d.id === selectedDraft.id) + 1} of {draftInvoices.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextDraft}
                    disabled={draftInvoices.findIndex(d => d.id === selectedDraft.id) === draftInvoices.length - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task 14: Revert Draft Confirmation Modal */}
      <AlertDialog open={showRevertConfirm} onOpenChange={setShowRevertConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert Draft Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the draft invoice from Xero and return all sales to uninvoiced status. This action cannot be undone.
              {draftToRevert && (
                <div className="mt-2 text-sm">
                  <strong>Invoice:</strong> {draftToRevert.xero_mapping?.xero_invoice_number || draftToRevert.invoice_no}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              autoFocus
              disabled={isReverting}
              onClick={() => setShowRevertConfirm(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRevertDraftConfirm} disabled={isReverting}>
              {isReverting ? 'Reverting...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task 14: Batch Authorise Confirmation Modal */}
      <AlertDialog open={showBatchAuthoriseConfirm} onOpenChange={setShowBatchAuthoriseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Authorise & Send Invoices?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedDraftIds.size} {selectedDraftIds.size === 1 ? 'invoice' : 'invoices'} will be sent totalling {formatCurrency(getSelectedDraftsTotalAmount())}. Send?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              autoFocus
              disabled={isAuthorising}
              onClick={() => setShowBatchAuthoriseConfirm(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleBatchAuthoriseConfirm} disabled={isAuthorising}>
              {isAuthorising ? 'Sending...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task 14: Batch Results Modal */}
      <Dialog open={showBatchResults} onOpenChange={setShowBatchResults}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Batch Authorise Results</DialogTitle>
          </DialogHeader>
          {batchResults && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {batchResults.summary.succeeded} succeeded, {batchResults.summary.failed} failed
                </span>
              </div>

              {batchResults.results.map((result, index) => (
                <div
                  key={result.invoiceId || index}
                  className={`p-3 rounded border ${
                    result.success
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {result.success ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className="font-medium font-mono text-sm">
                          {result.invoiceNumber}
                        </span>
                      </div>
                      {result.success && result.emailSent !== undefined && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {result.emailSent ? 'Email sent successfully' : 'Authorised (email pending)'}
                        </p>
                      )}
                      {!result.success && result.error && (
                        <p className="text-xs text-red-700 mt-1">{result.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchResults(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Xero Sync Diff Dialog (pre-authorise verification) */}
      <XeroSyncDiffDialog
        open={showSyncDiffDialog}
        onOpenChange={setShowSyncDiffDialog}
        differences={syncDiffData?.differences || []}
        customerName={syncDiffData?.customerName || ''}
        branchName={syncDiffData?.branchName || ''}
        syncing={syncDiffSyncing}
        onSyncAndProceed={handleSyncAndProceed}
        onProceedWithoutSync={handleProceedWithoutSync}
        onCancel={handleCancelSync}
      />

      {/* Customer Detail Modal (for quick edits from accruing table) */}
      <CustomerDetailModal
        customer={selectedCustomerForDetail}
        open={detailModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDetailModalOpen(false)
            setSelectedCustomerForDetail(null)
          }
        }}
        onSave={async () => {
          // Refresh uninvoiced data so warnings update
          await loadUninvoicedData()
        }}
      />
    </PageLayout>
  )
}